import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { customerApi, deviceApi, slaTemplateApi } from '../services/api';
import { getBaseUrl } from '../services/baseUrl';
import { showInAppAlert, showInAppConfirm } from '../services/inAppDialog';
import type { Customer, Device, DeviceSlaAssignment } from '../types';

type CustomerSort =
  | 'recent'
  | 'nameAsc'
  | 'nameDesc'
  | 'deviceCountDesc'
  | 'caseCountDesc'
  | 'locationCountDesc';

type CustomerStatusFilter = 'all' | 'active' | 'inactive';
type FilterOption = {
  value: string;
  label: string;
  searchText?: string;
};

type SearchableFilterProps = {
  styles: Record<string, any>;
  label: string;
  value: string;
  allLabel: string;
  searchPlaceholder: string;
  emptyLabel: string;
  options: FilterOption[];
  onChange: (value: string) => void;
};

type AssignableSlaTemplate = {
  id: string;
  deviceModelId: string | null;
  deviceModelName: string;
  name: string;
  brandingName: string;
  tierBrandingName: string;
  slaType: string;
  durationQuantity: number | null;
  durationPeriodUnit: string;
  status: string;
  hasSavedReport: boolean;
  savedReportAt: string | null;
};

type CustomerDeviceSlaReportAppendix = {
  key: string;
  kind: 'list' | 'schedule';
  title: string;
  summary: string;
  columns: string[];
  rows: string[][];
};

type CustomerDeviceSlaReportSection = {
  title: string;
  summary: string;
  includedItems: string[];
};

type CustomerDeviceSlaReport = {
  title: string;
  subtitle?: string;
  overview: string;
  sections: CustomerDeviceSlaReportSection[];
  appendices: CustomerDeviceSlaReportAppendix[];
  savedAt?: string | null;
};

type CustomerDeviceSlaEditorMode = 'view' | 'edit';

type CustomerDeviceFormState = {
  modelId: string;
  serialNumber: string;
  assetNumber: string;
  assetNumbersEnabled: boolean;
  locationId: string;
  manufacturedAt: string;
  installedAt: string;
  commissionedAt: string;
  warrantyStartAt: string;
  warrantyEndAt: string;
};

type CustomerDeviceEditFormState = {
  serialNumber: string;
  assetNumber: string;
  manufacturer: string;
  lifecycleStatus: string;
  manufacturedAt: string;
  installedAt: string;
  commissionedAt: string;
  warrantyStartAt: string;
  warrantyEndAt: string;
};

type CustomerDeviceDateField =
  | 'manufacturedAt'
  | 'installedAt'
  | 'commissionedAt'
  | 'warrantyStartAt'
  | 'warrantyEndAt';

type CustomerDeviceDateFields = Record<CustomerDeviceDateField, string>;

const DEVICE_LIFECYCLE_OPTIONS = ['INVENTORY', 'DEPLOYED', 'INACTIVE', 'RETIRED'];

const createEmptyCustomerDeviceForm = (assetNumbersEnabled = false): CustomerDeviceFormState => ({
  modelId: '',
  serialNumber: '',
  assetNumber: '',
  assetNumbersEnabled,
  locationId: '',
  manufacturedAt: '',
  installedAt: '',
  commissionedAt: '',
  warrantyStartAt: '',
  warrantyEndAt: ''
});

const EMPTY_CUSTOMER_DEVICE_EDIT_FORM: CustomerDeviceEditFormState = {
  serialNumber: '',
  assetNumber: '',
  manufacturer: '',
  lifecycleStatus: 'DEPLOYED',
  manufacturedAt: '',
  installedAt: '',
  commissionedAt: '',
  warrantyStartAt: '',
  warrantyEndAt: ''
};

const dateInputValueFromIso = (value?: string | null) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return trimmed.slice(0, 10);
};

const formatDisplayDate = (value?: string | null) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return 'Not set';
  const dateOnly = trimmed.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    return new Date(`${dateOnly}T00:00:00`).toLocaleDateString();
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return trimmed;
  return parsed.toLocaleDateString();
};

const parseDateOnlyValue = (value?: string | null) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  const dateOnly = trimmed.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null;
  const parsed = new Date(`${dateOnly}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

type CustomerLocationDisplayNode = {
  location: any;
  children: CustomerLocationDisplayNode[];
  index: number;
};

type CustomerLocationDisplayRow = {
  location: any;
  depth: number;
  parentPath: string;
};

const buildCustomerLocationDisplayRows = (locationsInput?: any[]): CustomerLocationDisplayRow[] => {
  const locations = Array.isArray(locationsInput) ? locationsInput : [];
  const nodes = new Map<string, CustomerLocationDisplayNode>();
  const roots: CustomerLocationDisplayNode[] = [];
  const rows: CustomerLocationDisplayRow[] = [];
  const visited = new Set<string>();

  locations.forEach((location, index) => {
    const id = String(location?.id || '').trim();
    if (!id) return;
    nodes.set(id, { location, children: [], index });
  });

  nodes.forEach(node => {
    const id = String(node.location?.id || '').trim();
    const parentId = String(node.location?.parentLocationId || '').trim();
    if (parentId && parentId !== id && nodes.has(parentId)) {
      nodes.get(parentId)!.children.push(node);
      return;
    }
    roots.push(node);
  });

  const sortNodes = (left: CustomerLocationDisplayNode, right: CustomerLocationDisplayNode) => {
    if (Boolean(left.location?.isPrimary) !== Boolean(right.location?.isPrimary)) {
      return left.location?.isPrimary ? -1 : 1;
    }
    return left.index - right.index;
  };

  const appendNode = (node: CustomerLocationDisplayNode, depth: number, parentPath: string) => {
    const id = String(node.location?.id || '').trim();
    if (!id || visited.has(id)) return;
    visited.add(id);
    rows.push({ location: node.location, depth, parentPath });
    const nextParentPath = parentPath
      ? `${parentPath} > ${String(node.location?.name || '').trim()}`
      : String(node.location?.name || '').trim();
    node.children.sort(sortNodes).forEach(child => appendNode(child, depth + 1, nextParentPath));
  };

  roots.sort(sortNodes).forEach(root => appendNode(root, 0, ''));
  nodes.forEach(node => appendNode(node, 0, ''));

  return rows;
};

const formatDateInputValue = (value?: Date | null) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addSlaDurationToDate = (
  startDateInput?: string | null,
  quantityInput?: number | null,
  unitInput?: string | null
) => {
  const startDate = parseDateOnlyValue(startDateInput);
  const quantity = Number(quantityInput || 0);
  const unit = String(unitInput || '').trim().toLowerCase();
  if (!startDate || !quantity || !unit) return null;
  const next = new Date(startDate.getTime());
  if (unit === 'hour' || unit === 'hours') {
    next.setHours(next.getHours() + quantity);
    return next;
  }
  if (unit === 'day' || unit === 'days') {
    next.setDate(next.getDate() + quantity);
    return next;
  }
  if (unit === 'week' || unit === 'weeks') {
    next.setDate(next.getDate() + quantity * 7);
    return next;
  }
  if (unit === 'month' || unit === 'months') {
    next.setMonth(next.getMonth() + quantity);
    return next;
  }
  if (unit === 'year' || unit === 'years') {
    next.setFullYear(next.getFullYear() + quantity);
    return next;
  }
  return null;
};

const customerDeviceDateLabels: Record<CustomerDeviceDateField, string> = {
  manufacturedAt: 'Manufactured At',
  installedAt: 'Installed At',
  commissionedAt: 'Commissioned At',
  warrantyStartAt: 'Warranty Start',
  warrantyEndAt: 'Warranty End'
};

const isDateInputBefore = (left?: string | null, right?: string | null) => {
  const leftDate = parseDateOnlyValue(left);
  const rightDate = parseDateOnlyValue(right);
  return Boolean(leftDate && rightDate && leftDate.getTime() < rightDate.getTime());
};

const getDefaultWarrantyEndAt = (startDateInput?: string | null) =>
  formatDateInputValue(addSlaDurationToDate(startDateInput, 1, 'year'));

const getDateRangeDurationMs = (startDateInput?: string | null, endDateInput?: string | null) => {
  const startDate = parseDateOnlyValue(startDateInput);
  const endDate = parseDateOnlyValue(endDateInput);
  if (!startDate || !endDate || endDate.getTime() < startDate.getTime()) return null;
  return endDate.getTime() - startDate.getTime();
};

const addDateRangeDuration = (startDateInput?: string | null, durationMs?: number | null) => {
  const startDate = parseDateOnlyValue(startDateInput);
  if (!startDate || typeof durationMs !== 'number') return '';
  return formatDateInputValue(new Date(startDate.getTime() + durationMs));
};

const shouldMoveDependentDate = (
  currentValue: string | undefined,
  previousAnchorValue: string | undefined,
  nextAnchorValue: string | undefined,
  options: { fillBlank: boolean }
) =>
  Boolean(
    nextAnchorValue &&
      ((options.fillBlank && !currentValue) ||
        (!!currentValue && currentValue === previousAnchorValue) ||
        isDateInputBefore(currentValue, nextAnchorValue))
  );

const applyCustomerDeviceDateAutomation = <T extends CustomerDeviceDateFields>(
  form: T,
  field: CustomerDeviceDateField,
  rawValue: string
): T => {
  const value = String(rawValue || '').trim();
  const next = { ...form, [field]: value } as T;

  const moveDependentDate = (
    sourceField: CustomerDeviceDateField,
    dependentField: CustomerDeviceDateField,
    options: { fillBlank: boolean; preserveOffset?: boolean }
  ) => {
    const anchorValue = String(next[sourceField] || '').trim();
    const dependentValue = String(next[dependentField] || '').trim();
    const previousAnchorValue = String(form[sourceField] || '').trim();
    const previousDependentValue = String(form[dependentField] || '').trim();
    const previousOffsetMs = getDateRangeDurationMs(previousAnchorValue, previousDependentValue);
    if (
      options.preserveOffset &&
      anchorValue &&
      previousAnchorValue &&
      previousDependentValue &&
      anchorValue !== previousAnchorValue &&
      previousOffsetMs !== null
    ) {
      next[dependentField] = addDateRangeDuration(anchorValue, previousOffsetMs);
      return;
    }
    if (shouldMoveDependentDate(dependentValue, form[sourceField], anchorValue, options)) {
      next[dependentField] = anchorValue;
    }
  };

  if (field === 'manufacturedAt' || field === 'installedAt') {
    moveDependentDate('manufacturedAt', 'installedAt', { fillBlank: false });
  }

  if (field === 'manufacturedAt' || field === 'installedAt' || next.installedAt !== form.installedAt) {
    moveDependentDate('installedAt', 'commissionedAt', { fillBlank: true, preserveOffset: true });
  }

  if (
    field === 'manufacturedAt' ||
    field === 'installedAt' ||
    field === 'commissionedAt' ||
    next.commissionedAt !== form.commissionedAt
  ) {
    moveDependentDate('commissionedAt', 'warrantyStartAt', { fillBlank: true, preserveOffset: true });
  }

  if (field === 'warrantyStartAt' && value) {
    next.warrantyStartAt = value;
  }

  const nextWarrantyStart = next.warrantyStartAt;
  if (nextWarrantyStart && nextWarrantyStart !== form.warrantyStartAt) {
    const previousWarrantyDurationMs = getDateRangeDurationMs(form.warrantyStartAt, form.warrantyEndAt);
    const movedWarrantyEnd = previousWarrantyDurationMs !== null
      ? addDateRangeDuration(nextWarrantyStart, previousWarrantyDurationMs)
      : getDefaultWarrantyEndAt(nextWarrantyStart);
    const shouldMoveWarrantyEnd =
      !form.warrantyEndAt ||
      Boolean(movedWarrantyEnd && next.warrantyEndAt === form.warrantyEndAt) ||
      isDateInputBefore(form.warrantyEndAt, nextWarrantyStart);
    if (shouldMoveWarrantyEnd) {
      next.warrantyEndAt = movedWarrantyEnd;
    }
  }

  return next;
};

const validateCustomerDeviceDateSequence = (form: CustomerDeviceDateFields) => {
  const normalized: CustomerDeviceDateFields = {
    manufacturedAt: String(form.manufacturedAt || '').trim(),
    installedAt: String(form.installedAt || '').trim(),
    commissionedAt: String(form.commissionedAt || '').trim(),
    warrantyStartAt: String(form.warrantyStartAt || '').trim(),
    warrantyEndAt: String(form.warrantyEndAt || '').trim()
  };

  for (const field of Object.keys(customerDeviceDateLabels) as CustomerDeviceDateField[]) {
    if (normalized[field] && !parseDateOnlyValue(normalized[field])) {
      return `${customerDeviceDateLabels[field]} is not a valid date.`;
    }
  }

  if (isDateInputBefore(normalized.installedAt, normalized.manufacturedAt)) {
    return 'Installed At cannot be earlier than Manufactured At. A device cannot be installed before it was manufactured.';
  }
  if (isDateInputBefore(normalized.commissionedAt, normalized.installedAt)) {
    return 'Commissioned At cannot be earlier than Installed At. A device must be installed before it is commissioned.';
  }
  if (!normalized.installedAt && isDateInputBefore(normalized.commissionedAt, normalized.manufacturedAt)) {
    return 'Commissioned At cannot be earlier than Manufactured At. A device cannot be commissioned before it exists.';
  }
  if (isDateInputBefore(normalized.warrantyStartAt, normalized.commissionedAt)) {
    return 'Warranty Start cannot be earlier than Commissioned At. Warranty should start on or after commissioning.';
  }
  if (!normalized.commissionedAt && isDateInputBefore(normalized.warrantyStartAt, normalized.installedAt)) {
    return 'Warranty Start cannot be earlier than Installed At when no Commissioned At date is set.';
  }
  if (
    !normalized.commissionedAt &&
    !normalized.installedAt &&
    isDateInputBefore(normalized.warrantyStartAt, normalized.manufacturedAt)
  ) {
    return 'Warranty Start cannot be earlier than Manufactured At when install and commission dates are not set.';
  }
  if (isDateInputBefore(normalized.warrantyEndAt, normalized.warrantyStartAt)) {
    return 'Warranty End cannot be earlier than Warranty Start.';
  }

  return '';
};

const normalizeCustomerDeviceSlaReport = (input: any): CustomerDeviceSlaReport => {
  const safe = input && typeof input === 'object' ? input : {};
  const sections = Array.isArray(safe.sections)
    ? safe.sections.map((section: any) => ({
        title: String(section?.title || '').trim() || 'Section',
        summary: String(section?.summary || '').trim(),
        includedItems: Array.isArray(section?.includedItems)
          ? section.includedItems
              .map((item: any) => String(item || '').trim())
              .filter(Boolean)
          : []
      }))
    : [];
  const appendices = Array.isArray(safe.appendices)
    ? safe.appendices.map((appendix: any) => ({
        key: String(appendix?.key || appendix?.title || '').trim() || 'appendix',
        kind: appendix?.kind === 'schedule' ? 'schedule' : 'list',
        title: String(appendix?.title || '').trim() || 'Appendix',
        summary: String(appendix?.summary || '').trim(),
        columns: Array.isArray(appendix?.columns)
          ? appendix.columns.map((column: any) => String(column || '').trim()).filter(Boolean)
          : [],
        rows: Array.isArray(appendix?.rows)
          ? appendix.rows.map((row: any) =>
              Array.isArray(row) ? row.map((cell: any) => String(cell ?? '').trim()) : []
            )
          : []
      }))
    : [];
  return {
    title: String(safe.title || '').trim() || 'Customer-facing SLA agreement',
    subtitle: String(safe.subtitle || '').trim(),
    overview: String(safe.overview || '').trim(),
    sections,
    appendices,
    savedAt: String(safe.savedAt || '').trim() || null
  };
};

const getTodayDateInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function SearchableFilter({
  styles,
  label,
  value,
  allLabel,
  searchPlaceholder,
  emptyLabel,
  options,
  onChange
}: SearchableFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const selectedLabel =
    value === 'all'
      ? allLabel
      : options.find(option => option.value === value)?.label || allLabel;

  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(option =>
      `${option.label} ${option.searchText || ''}`.toLowerCase().includes(needle)
    );
  }, [options, query]);

  return (
    <div style={styles.searchableFilter} ref={rootRef}>
      <div style={styles.searchableFilterLabel}>{label}</div>
      <button
        type="button"
        style={{
          ...styles.searchableFilterTrigger,
          ...(open ? styles.searchableFilterTriggerOpen : {})
        }}
        onClick={() => setOpen(current => !current)}
      >
        <span style={styles.searchableFilterTriggerValue}>{selectedLabel}</span>
        <span style={styles.searchableFilterTriggerIcon}>{open ? '^' : 'v'}</span>
      </button>
      {open && (
        <div style={styles.searchableFilterPopover}>
          <input
            type="text"
            placeholder={searchPlaceholder}
            style={styles.searchableFilterSearchInput}
            value={query}
            onChange={event => setQuery(event.target.value)}
            autoFocus
          />
          <div style={styles.searchableFilterOptions}>
            <button
              type="button"
              style={{
                ...styles.searchableFilterOption,
                ...(value === 'all' ? styles.searchableFilterOptionActive : {})
              }}
              onClick={() => {
                onChange('all');
                setOpen(false);
              }}
            >
              {allLabel}
            </button>
            {filteredOptions.length === 0 ? (
              <div style={styles.searchableFilterEmpty}>{emptyLabel}</div>
            ) : (
              filteredOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  style={{
                    ...styles.searchableFilterOption,
                    ...(value === option.value ? styles.searchableFilterOptionActive : {})
                  }}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export const TechDashboardCustomersTab = React.memo(function TechDashboardCustomersTab(props: any) {
  const {
    addingCustomer,
    canManageSerializedDevices,
    canReadDevices,
    canManageCustomers,
    canAccessSla,
    customers,
    customerSearch,
    customerSort,
    customerStatusFilter,
    customerQuickFilter,
    customerQuickOptions,
    customerLocationFilter,
    customerLocationOptions,
    customerPage,
    customerPageCount,
    customerPageSize,
    customerPageStart,
    customerPageEnd,
    editCustomer,
    editingCustomerId,
    filteredCustomers,
    paginatedCustomers,
    handleCancelEditCustomer,
    handleCreateCustomer,
    handleInvitePortalUser,
    handleSaveCustomerNotes,
    handleSetCustomerActive,
    handleSetPortalUserActive,
    handleSaveEditedCustomer,
    handleSetPrimaryLocation,
    handleSubmitContact,
    handleSubmitLocation,
    newCustomer,
    contactFormCustomerId,
    contactForm,
    locationFormCustomerId,
    locationForm,
    openDevicesTabForCustomer,
    openContactForm,
    openLocationForm,
    portalContactActionKey,
    resetNewCustomerForm,
    resetContactForm,
    savingCustomerId,
    savingContact,
    setActiveTab,
    setCustomerFilter,
    setCustomerPage,
    setCustomerPageSize,
    setCustomerLocationFilter,
    setCustomerQuickFilter,
    setCustomerSearch,
    setCustomerSort,
    setCustomerStatusFilter,
    setContactForm,
    setEditCustomer,
    setLocationForm,
    setNewCustomer,
    setShowAddCustomerForm,
    showAddCustomerForm,
    startEditCustomer,
    styles,
    deviceModels,
    customerDetailsById,
    customerDetailLoadingId,
    customerDetailError,
    loadCustomerDetail,
    refreshCustomerState,
    resetLocationForm,
    savingLocation,
    clearCustomerFilters,
    hasActiveCustomerFilters,
    customerQuickViewRequest,
    clearCustomerQuickViewRequest,
    topPanelCollapseDefault = false
  } = props;

  const [customerQuickViewId, setCustomerQuickViewId] = useState<string | null>(null);
  const [customersTopPanelCollapsed, setCustomersTopPanelCollapsed] = useState(
    Boolean(topPanelCollapseDefault)
  );
  const [showCustomerNotes, setShowCustomerNotes] = useState(false);
  const [editingCustomerNotes, setEditingCustomerNotes] = useState(false);
  const [customerNotesDraft, setCustomerNotesDraft] = useState('');
  const [showCustomerContacts, setShowCustomerContacts] = useState(false);
  const [showCustomerContactEditor, setShowCustomerContactEditor] = useState(false);
  const [showCustomerLocations, setShowCustomerLocations] = useState(false);
  const [showCustomerLocationEditor, setShowCustomerLocationEditor] = useState(false);
  const [activeCustomerDeviceModelKey, setActiveCustomerDeviceModelKey] = useState<string | null>(null);
  const [showCustomerAddDevice, setShowCustomerAddDevice] = useState(false);
  const [showCustomerRelocateDevice, setShowCustomerRelocateDevice] = useState(false);
  const [showCustomerDeviceSlaEditor, setShowCustomerDeviceSlaEditor] = useState(false);
  const [customerDeviceSlaEditorMode, setCustomerDeviceSlaEditorMode] =
    useState<CustomerDeviceSlaEditorMode>('edit');
  const [customerDeviceSlaDeviceId, setCustomerDeviceSlaDeviceId] = useState<string | null>(null);
  const [customerDeviceSlaAssignmentId, setCustomerDeviceSlaAssignmentId] = useState<string | null>(null);
  const [customerDeviceSlaForm, setCustomerDeviceSlaForm] = useState({
    slaTemplateId: '',
    startDate: getTodayDateInputValue(),
    endDate: ''
  });
  const [savingCustomerDeviceSla, setSavingCustomerDeviceSla] = useState(false);
  const [customerDeviceSlaError, setCustomerDeviceSlaError] = useState('');
  const [availableSlaTemplates, setAvailableSlaTemplates] = useState<AssignableSlaTemplate[]>([]);
  const [loadingSlaTemplates, setLoadingSlaTemplates] = useState(false);
  const [customerDeviceSlaReport, setCustomerDeviceSlaReport] = useState<CustomerDeviceSlaReport | null>(null);
  const [customerDeviceSlaReportLoadingId, setCustomerDeviceSlaReportLoadingId] = useState<string | null>(null);
  const [customerDeviceForm, setCustomerDeviceForm] = useState<CustomerDeviceFormState>(
    createEmptyCustomerDeviceForm(false)
  );
  const [savingCustomerDevice, setSavingCustomerDevice] = useState(false);
  const [customerDeviceError, setCustomerDeviceError] = useState('');
  const [customerDeviceEditId, setCustomerDeviceEditId] = useState<string | null>(null);
  const [customerDeviceEditForm, setCustomerDeviceEditForm] = useState<CustomerDeviceEditFormState>(
    EMPTY_CUSTOMER_DEVICE_EDIT_FORM
  );
  const [savingCustomerDeviceEdit, setSavingCustomerDeviceEdit] = useState(false);
  const [customerDeviceEditError, setCustomerDeviceEditError] = useState('');
  const [customerRelocateDeviceForm, setCustomerRelocateDeviceForm] = useState({
    deviceId: '',
    action: 'move',
    nextLocationId: '',
    removeTarget: 'inventory'
  });
  const [savingCustomerRelocateDevice, setSavingCustomerRelocateDevice] = useState(false);
  const [customerRelocateDeviceError, setCustomerRelocateDeviceError] = useState('');
  const [customerImagePreviewUrl, setCustomerImagePreviewUrl] = useState<string | null>(null);
  const [customerImagePreviewName, setCustomerImagePreviewName] = useState('');
  const [customerImagePreviewFile, setCustomerImagePreviewFileState] = useState<File | null>(null);
  const [customerImageCurrentUrl, setCustomerImageCurrentUrl] = useState<string | null>(null);
  const customerImageInputRef = useRef<HTMLInputElement | null>(null);
  const [deviceImageBaseUrl, setDeviceImageBaseUrl] = useState('');

  const customerSortOptions = [
    { value: 'recent', label: 'Newest first' },
    { value: 'nameAsc', label: 'Name A-Z' },
    { value: 'nameDesc', label: 'Name Z-A' },
    { value: 'deviceCountDesc', label: 'Most devices' },
    { value: 'caseCountDesc', label: 'Most cases' },
    { value: 'locationCountDesc', label: 'Most locations' }
  ];

  const getCustomerDisplayName = useCallback(
    (customer?: Customer | null) =>
      String(customer?.companyName ?? customer?.company ?? 'Unnamed Customer').trim() ||
      'Unnamed Customer',
    []
  );

  const getCustomerCount = useCallback(
    (
      customer: Customer | null | undefined,
      key: 'devices' | 'cases' | 'contacts' | 'locations'
    ) => {
      if (!customer) return 0;
      return customer._count?.[key] ?? customer[key]?.length ?? 0;
    },
    []
  );

  const getPrimaryLocation = useCallback((customer?: Customer | null) => {
    const locations = Array.isArray(customer?.locations) ? customer?.locations ?? [] : [];
    return (
      locations.find(location => location?.isPrimary) ||
      locations.find(location => location?.isActive !== false) ||
      locations[0] ||
      null
    );
  }, []);

  const getLocationSummary = useCallback(
    (customer?: Customer | null) => {
      const primaryLocation = getPrimaryLocation(customer);
      if (primaryLocation) {
        const parts = [
          primaryLocation.name,
          primaryLocation.addressLine1 || '',
          primaryLocation.addressLine2 || '',
          primaryLocation.city || '',
          primaryLocation.stateProvince || '',
          primaryLocation.postalCode || '',
          primaryLocation.country || ''
        ]
          .map((value: any) => String(value || '').trim())
          .filter(Boolean);
        if (parts.length > 0) return parts.join(' | ');
      }
      return 'Primary location not set';
    },
    [getPrimaryLocation]
  );

  const getLocationLine = useCallback((location?: any) => {
    if (!location) return 'Location details not available';
    const parts = [
      location.addressLine1 || '',
      location.addressLine2 || '',
      location.city || '',
      location.stateProvince || '',
      location.postalCode || '',
      location.country || ''
    ]
      .map((value: any) => String(value || '').trim())
      .filter(Boolean);
    return parts.join(' | ') || 'Location details not available';
  }, []);

  const getSlaPeriodLabel = useCallback((template?: any) => {
    const quantity = Number(template?.durationQuantity || 0);
    const unit = String(template?.durationPeriodUnit || '').trim();
    if (!quantity || !unit) return 'Period not set';
    return `${quantity} ${unit}`;
  }, []);

  const getSlaDisplayName = useCallback((template?: any) => {
    if (!template) return 'No SLA assigned';
    const brand = String(template?.brandingName || '').trim();
    const tier = String(template?.tierBrandingName || '').trim();
    const type = String(template?.slaType || '').trim();
    const identity = [brand, tier].filter(Boolean).join(' ');
    return [identity || null, type || null].filter(Boolean).join(' | ') || 'Assigned SLA';
  }, []);

  const getSlaResolvedEndDateInputValue = useCallback(
    (assignment?: DeviceSlaAssignment | null, template?: any | null, startDateOverride?: string | null) => {
      const explicitEndDate = dateInputValueFromIso(assignment?.endDate);
      if (explicitEndDate) return explicitEndDate;
      return formatDateInputValue(
        addSlaDurationToDate(
          startDateOverride || dateInputValueFromIso(assignment?.startDate),
          Number(template?.durationQuantity || 0) || null,
          String(template?.durationPeriodUnit || '').trim()
        )
      );
    },
    []
  );

  const getSlaAssignmentTiming = useCallback(
    (assignment?: DeviceSlaAssignment | null, template?: any | null) => {
      const startDate = dateInputValueFromIso(assignment?.startDate);
      const endDate = getSlaResolvedEndDateInputValue(assignment, template);
      const today = getTodayDateInputValue();
      if (!startDate) {
        return {
          label: 'Not active',
          tone: '#64748b',
          background: '#f8fafc',
          border: '#cbd5e1',
          isExpired: false
        };
      }
      if (endDate && endDate < today) {
        return {
          label: 'Ended',
          tone: '#7c2d12',
          background: '#fff7ed',
          border: '#fdba74',
          isExpired: true
        };
      }
      if (startDate > today) {
        return {
          label: 'Scheduled',
          tone: '#1d4ed8',
          background: '#eff6ff',
          border: '#93c5fd',
          isExpired: false
        };
      }
      return {
        label: 'Active',
        tone: '#166534',
        background: '#f0fdf4',
        border: '#86efac',
        isExpired: false
      };
    },
    [getSlaResolvedEndDateInputValue]
  );

  const getVisibleDeviceSlaAssignments = useCallback(
    (device?: any) =>
      (Array.isArray(device?.slaAssignments) ? device.slaAssignments : [])
        .filter((assignment: DeviceSlaAssignment | null | undefined) => {
          if (!assignment?.slaTemplate) return false;
          return !getSlaAssignmentTiming(assignment, assignment.slaTemplate).isExpired;
        })
        .sort((a: DeviceSlaAssignment, b: DeviceSlaAssignment) => {
          const startA = String(a?.startDate || '');
          const startB = String(b?.startDate || '');
          if (startA !== startB) return startB.localeCompare(startA);
          return String(b?.assignedAt || '').localeCompare(String(a?.assignedAt || ''));
        }),
    [getSlaAssignmentTiming]
  );

  const getSlaTemplateOptionLabel = useCallback(
    (template?: AssignableSlaTemplate | null) => {
      if (!template) return 'Unnamed SLA template';
      const identity = [template.brandingName, template.tierBrandingName].filter(Boolean).join(' ');
      return [
        identity || template.name,
        template.slaType || null,
        getSlaPeriodLabel(template),
        template.name && identity !== template.name ? template.name : null
      ]
        .filter(Boolean)
        .join(' | ');
    },
    [getSlaPeriodLabel]
  );

  const getInitials = useCallback(
    (customer?: Customer | null) => {
      const initials = getCustomerDisplayName(customer)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('');
      return initials || 'CU';
    },
    [getCustomerDisplayName]
  );

  const activeCustomerSummary = useMemo(() => {
    if (!customerQuickViewId) return null;
    return (
      customers.find((customer: Customer) => String(customer.id || '').trim() === customerQuickViewId) ||
      null
    );
  }, [customerQuickViewId, customers]);

  const activeCustomerDetail = customerQuickViewId
    ? customerDetailsById[customerQuickViewId] || null
    : null;
  const activeCustomer = activeCustomerDetail || activeCustomerSummary || null;
  const activeCustomerLoading =
    Boolean(customerQuickViewId) &&
    customerDetailLoadingId === customerQuickViewId &&
    !activeCustomerDetail;
  const activeCustomerError =
    customerDetailError?.customerId === customerQuickViewId ? customerDetailError.message : null;

  useEffect(() => {
    if (!customerQuickViewId) return;
    if (customerDetailsById[customerQuickViewId]) return;
    void loadCustomerDetail(customerQuickViewId).catch((error: any) => {
      console.warn('Failed to load customer detail:', error);
    });
  }, [customerDetailsById, customerQuickViewId, loadCustomerDetail]);

  useEffect(() => {
    let mounted = true;
    void getBaseUrl()
      .then(base => {
        if (mounted) setDeviceImageBaseUrl(base);
      })
      .catch(() => {
        if (mounted) setDeviceImageBaseUrl('');
      });
    return () => {
      mounted = false;
    };
  }, []);

  const customerStats = {
    active: filteredCustomers.filter((customer: Customer) => customer.isActive).length,
    inactive: filteredCustomers.filter((customer: Customer) => !customer.isActive).length,
    withDevices: filteredCustomers.filter(
      (customer: Customer) => getCustomerCount(customer, 'devices') > 0
    ).length,
    withCases: filteredCustomers.filter(
      (customer: Customer) => getCustomerCount(customer, 'cases') > 0
    ).length
  };

  const activeCustomerCounts = {
    devices: getCustomerCount(activeCustomer, 'devices'),
    cases: getCustomerCount(activeCustomer, 'cases'),
    contacts: getCustomerCount(activeCustomer, 'contacts'),
    locations: getCustomerCount(activeCustomer, 'locations')
  };

  const activeCustomerCaseStatuses = useMemo(() => {
    const buckets = { open: 0, inProgress: 0, onHold: 0, resolved: 0, closed: 0 };
    (activeCustomer?.cases || []).forEach((caseItem: any) => {
      const status = String(caseItem?.status || '').trim().toUpperCase();
      if (status === 'RESOLVED') buckets.resolved += 1;
      else if (status === 'CLOSED') buckets.closed += 1;
      else if (status === 'ON_HOLD' || status.includes('HOLD')) buckets.onHold += 1;
      else if (status.includes('PROGRESS')) buckets.inProgress += 1;
      else buckets.open += 1;
    });
    return buckets;
  }, [activeCustomer]);

  const activeCustomerCaseCountByDeviceId = useMemo(() => {
    const counts = new Map<string, number>();
    (activeCustomer?.cases || []).forEach((caseItem: any) => {
      const deviceId = String(caseItem?.deviceId || '').trim();
      if (!deviceId) return;
      counts.set(deviceId, (counts.get(deviceId) || 0) + 1);
    });
    return counts;
  }, [activeCustomer]);

  const availableDeviceModels = useMemo(() => {
    return [...(deviceModels || [])]
      .filter((model: any) => String(model?.id || '').trim())
      .sort((a: any, b: any) => {
        const labelA = String(a?.modelNumber || a?.modelName || '').trim();
        const labelB = String(b?.modelNumber || b?.modelName || '').trim();
        return labelA.localeCompare(labelB);
      });
  }, [deviceModels]);

  const activeCustomerDeviceModels = useMemo(() => {
    const modelMap = new Map<
      string,
      { key: string; modelId: string; label: string; devices: Device[] }
    >();
    (activeCustomer?.devices || []).forEach((device: any) => {
      const modelId = String(device?.modelId || device?.model?.id || '').trim();
      const label =
        String(device?.model?.modelNumber || device?.modelNumber || 'Unknown model').trim() ||
        'Unknown model';
      const key = modelId || label;
      const existing = modelMap.get(key) || { key, modelId, label, devices: [] };
      existing.devices.push(device);
      modelMap.set(key, existing);
    });
    return Array.from(modelMap.values())
      .map(entry => ({
        key: entry.key,
        modelId: entry.modelId,
        label: entry.label,
        imageUrl:
          (entry.devices.find((device: any) => String((device as any)?.model?.imageUrl || '').trim()) as any)?.model
            ?.imageUrl ||
          availableDeviceModels.find((model: any) => String(model?.id || '').trim() === entry.modelId)?.imageUrl ||
          availableDeviceModels.find(
            (model: any) =>
              String(model?.modelNumber || model?.modelName || '').trim().toLowerCase() ===
              String(entry.label || '').trim().toLowerCase()
          )?.imageUrl ||
          null,
        deviceCount: entry.devices.length,
        devices: entry.devices
      }))
      .sort((a, b) => b.deviceCount - a.deviceCount || a.label.localeCompare(b.label));
  }, [activeCustomer, availableDeviceModels]);
  const activeCustomerDeviceModel = useMemo(
    () =>
      activeCustomerDeviceModels.find(
        (model: any) => String(model.key || '').trim() === String(activeCustomerDeviceModelKey || '').trim()
      ) || null,
    [activeCustomerDeviceModelKey, activeCustomerDeviceModels]
  );
  const activeCustomerAssignedDevices = useMemo(() => {
    const useAssetNumbers = Boolean(activeCustomer?.assetNumbersEnabled);
    return [...(activeCustomer?.devices || [])].sort((a: any, b: any) => {
      const modelA = String(a?.model?.modelNumber || a?.modelNumber || '').trim();
      const modelB = String(b?.model?.modelNumber || b?.modelNumber || '').trim();
      if (modelA !== modelB) return modelA.localeCompare(modelB);
      if (useAssetNumbers) {
        const assetA = String(a?.assetNumber || '').trim();
        const assetB = String(b?.assetNumber || '').trim();
        if (assetA || assetB) return assetA.localeCompare(assetB);
      }
      return String(a?.serialNumber || '').localeCompare(String(b?.serialNumber || ''));
    });
  }, [activeCustomer]);
  const getCustomerDeviceIdentityLabel = useCallback(
    (device?: Device | null) => {
      const serial = String(device?.serialNumber || '').trim() || 'Unknown serial';
      const asset = String(device?.assetNumber || '').trim();
      if (activeCustomer?.assetNumbersEnabled && asset) {
        return `Asset ${asset}`;
      }
      return `Serial ${serial}`;
    },
    [activeCustomer?.assetNumbersEnabled]
  );
  const getCustomerDeviceIdentity = useCallback(
    (device?: Device | null) => {
      const serial = String(device?.serialNumber || '').trim() || 'Unknown serial';
      const asset = String(device?.assetNumber || '').trim();
      if (activeCustomer?.assetNumbersEnabled && asset) {
        return {
          primaryLabel: 'Asset',
          primaryValue: asset,
          secondaryLabel: 'Serial',
          secondaryValue: serial
        };
      }
      return {
        primaryLabel: 'Serial',
        primaryValue: serial,
        secondaryLabel: '',
        secondaryValue: ''
      };
    },
    [activeCustomer?.assetNumbersEnabled]
  );
  const activeCustomerSlaDevice = useMemo(
    () =>
      activeCustomerAssignedDevices.find(
        (device: any) => String(device?.id || '').trim() === String(customerDeviceSlaDeviceId || '').trim()
      ) || null,
    [activeCustomerAssignedDevices, customerDeviceSlaDeviceId]
  );
  const activeCustomerEditingDevice = useMemo(
    () =>
      activeCustomerAssignedDevices.find(
        (device: any) => String(device?.id || '').trim() === String(customerDeviceEditId || '').trim()
      ) || null,
    [activeCustomerAssignedDevices, customerDeviceEditId]
  );
  const activeCustomerVisibleSlaAssignments = useMemo(
    () => getVisibleDeviceSlaAssignments(activeCustomerSlaDevice),
    [activeCustomerSlaDevice, getVisibleDeviceSlaAssignments]
  );
  const activeCustomerSlaAssignment = useMemo(
    () =>
      (activeCustomerVisibleSlaAssignments.find(
        (assignment: DeviceSlaAssignment) =>
          String(assignment?.id || '').trim() === String(customerDeviceSlaAssignmentId || '').trim()
      ) || null) as DeviceSlaAssignment | null,
    [customerDeviceSlaAssignmentId, activeCustomerVisibleSlaAssignments]
  );
  const activeCustomerVisibleSlaAssignment = activeCustomerSlaAssignment;
  const customerDeviceSlaModalCardStyle = {
    ...styles.caseQuickViewCard,
    ...styles.customerAddDeviceModalCard,
    width: 'min(1120px, 96vw)',
    maxHeight: 'min(88vh, 920px)',
    gap: '1rem'
  };
  const customerDeviceSlaSummaryGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'minmax(260px, 1.8fr) repeat(4, minmax(120px, 1fr))',
    gap: '0.85rem',
    alignItems: 'start',
    minWidth: '920px'
  };
  const loadAssignableSlaTemplates = useCallback(async () => {
    if (!canAccessSla) return;
    setLoadingSlaTemplates(true);
    try {
      const templates = await slaTemplateApi.getAll();
      const normalized = Array.isArray(templates)
        ? templates.map((template: any) => ({
            id: String(template?.id || '').trim(),
            deviceModelId: String(template?.deviceModelId || '').trim() || null,
            deviceModelName:
              String(template?.deviceModelName || '').trim() ||
              String(template?.deviceModel || '').trim(),
            name: String(template?.name || '').trim(),
            brandingName: String(template?.brandingName || '').trim(),
            tierBrandingName: String(template?.tierBrandingName || '').trim(),
            slaType: String(template?.slaType || '').trim(),
            durationQuantity: Number(template?.durationQuantity || 0) || null,
            durationPeriodUnit: String(template?.durationPeriodUnit || '').trim(),
            status: String(template?.status || '').trim(),
            hasSavedReport: Boolean(template?.hasSavedReport),
            savedReportAt: template?.savedReportAt || null
          }))
        : [];
      setAvailableSlaTemplates(
        normalized.sort((a, b) => {
          const brandCompare = a.brandingName.localeCompare(b.brandingName);
          if (brandCompare !== 0) return brandCompare;
          const tierCompare = a.tierBrandingName.localeCompare(b.tierBrandingName);
          if (tierCompare !== 0) return tierCompare;
          return a.name.localeCompare(b.name);
        })
      );
    } catch (error: any) {
      setCustomerDeviceSlaError(error?.message || 'Failed to load SLA templates.');
    } finally {
      setLoadingSlaTemplates(false);
    }
  }, [canAccessSla]);
  const eligibleSlaTemplatesForDevice = useMemo(() => {
    if (!activeCustomerSlaDevice) return [];
    const deviceModelId = String(activeCustomerSlaDevice?.modelId || activeCustomerSlaDevice?.model?.id || '').trim();
    const deviceModelName = String(
      activeCustomerSlaDevice?.model?.modelNumber || activeCustomerSlaDevice?.modelNumber || ''
    )
      .trim()
      .toLowerCase();
    const currentTemplate = activeCustomerVisibleSlaAssignment?.slaTemplate || null;
    const filtered = availableSlaTemplates.filter(template => {
      const status = String(template.status || '').trim().toUpperCase();
      if (status !== 'ACTIVE') return false;
      const templateModelId = String(template.deviceModelId || '').trim();
      const templateModelName = String(template.deviceModelName || '').trim().toLowerCase();
      if (deviceModelId && templateModelId) return templateModelId === deviceModelId;
      return Boolean(templateModelName && deviceModelName && templateModelName === deviceModelName);
    });

    if (
      currentTemplate?.id &&
      !filtered.some(template => String(template.id || '').trim() === String(currentTemplate.id || '').trim())
    ) {
      filtered.unshift({
        id: String(currentTemplate.id || '').trim(),
        deviceModelId: String(currentTemplate.deviceModelId || '').trim() || null,
        deviceModelName: String(currentTemplate.deviceModelName || '').trim(),
        name: String(currentTemplate.name || '').trim(),
        brandingName: String(currentTemplate.brandingName || '').trim(),
        tierBrandingName: String(currentTemplate.tierBrandingName || '').trim(),
        slaType: String(currentTemplate.slaType || '').trim(),
        durationQuantity: Number(currentTemplate.durationQuantity || 0) || null,
        durationPeriodUnit: String(currentTemplate.durationPeriodUnit || '').trim(),
        status: String(currentTemplate.status || '').trim(),
        hasSavedReport: Boolean(
          currentTemplate.hasSavedReport ||
            currentTemplate.savedReportAt ||
            currentTemplate.customerReportSavedAt
        ),
        savedReportAt:
          currentTemplate.savedReportAt ||
          currentTemplate.customerReportSavedAt ||
          null
      });
    }

    return filtered;
  }, [activeCustomerVisibleSlaAssignment, activeCustomerSlaDevice, availableSlaTemplates]);
  const selectedCustomerDeviceSlaTemplate = useMemo(
    () =>
      eligibleSlaTemplatesForDevice.find(
        template =>
          String(template.id || '').trim() === String(customerDeviceSlaForm.slaTemplateId || '').trim()
      ) || null,
    [customerDeviceSlaForm.slaTemplateId, eligibleSlaTemplatesForDevice]
  );
  const handleOpenDevicesWorkspace = useCallback(async () => {
    if (!activeCustomer?.id) return;

    const detailedCustomer =
      activeCustomerDetail ||
      (await loadCustomerDetail(activeCustomer.id, { force: true }).catch((error: any) => {
        console.warn('Failed to prepare customer devices workspace:', error);
        return null;
      }));

    openDevicesTabForCustomer({
      customerId: String(activeCustomer.id || '').trim(),
      customerName:
        String(activeCustomer.companyName ?? (activeCustomer as any).company ?? 'Unnamed Customer').trim() ||
        'Unnamed Customer',
      devices: Array.isArray(detailedCustomer?.devices)
        ? detailedCustomer.devices
        : Array.isArray(activeCustomer.devices)
          ? activeCustomer.devices
          : []
    });
    setCustomerQuickViewId(null);
  }, [activeCustomer, activeCustomerDetail, loadCustomerDetail, openDevicesTabForCustomer]);
  const activeRelocateDevice = useMemo(
    () =>
      activeCustomerAssignedDevices.find(
        (device: any) =>
          String(device?.id || '').trim() === String(customerRelocateDeviceForm.deviceId || '').trim()
      ) || null,
    [activeCustomerAssignedDevices, customerRelocateDeviceForm.deviceId]
  );

  const activeCustomerContacts = useMemo(() => {
    return [...(activeCustomer?.contacts || [])].sort((a: any, b: any) => {
      if (a?.isPrimary && !b?.isPrimary) return -1;
      if (!a?.isPrimary && b?.isPrimary) return 1;
      if (a?.isActive && !b?.isActive) return -1;
      if (!a?.isActive && b?.isActive) return 1;
      return String(a?.fullName || '').localeCompare(String(b?.fullName || ''));
    });
  }, [activeCustomer]);

  const activeCustomerLocations = useMemo(() => {
    const primaryLocationId = String(getPrimaryLocation(activeCustomer)?.id || '').trim();
    return [...(activeCustomer?.locations || [])].sort((a: any, b: any) => {
      const aIsPrimary = String(a?.id || '').trim() === primaryLocationId;
      const bIsPrimary = String(b?.id || '').trim() === primaryLocationId;
      if (aIsPrimary && !bIsPrimary) return -1;
      if (!aIsPrimary && bIsPrimary) return 1;
      if (a?.isActive && !b?.isActive) return -1;
      if (!a?.isActive && b?.isActive) return 1;
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
  }, [activeCustomer, getPrimaryLocation]);
  const activeCustomerLocationRows = useMemo(
    () => buildCustomerLocationDisplayRows(activeCustomerLocations),
    [activeCustomerLocations]
  );
  const activeCustomerLocationIds = useMemo(
    () =>
      activeCustomerLocationRows
        .map(({ location }) => String(location?.id || '').trim())
        .filter((locationId: string) => locationId.length > 0),
    [activeCustomerLocationRows]
  );
  const contactFormLocationIds = useMemo(() => {
    const rawIds = Array.isArray(contactForm?.locationIds)
      ? contactForm.locationIds
      : (contactForm?.locationId ? [contactForm.locationId] : []);
    return Array.from(
      new Set<string>(
        rawIds
          .map((value: unknown) => String(value || '').trim())
          .filter((locationId: string) => locationId.length > 0)
      )
    );
  }, [contactForm?.locationId, contactForm?.locationIds]);
  const contactFormHasAllLocations = useMemo(
    () =>
      activeCustomerLocationIds.length > 0 &&
      activeCustomerLocationIds.every(locationId => contactFormLocationIds.includes(locationId)),
    [activeCustomerLocationIds, contactFormLocationIds]
  );
  const activeCustomerLocationDisplayById = useMemo(() => {
    const displayById = new Map<
      string,
      {
        depth: number;
        displayPath: string;
        parentPath: string;
      }
    >();
    activeCustomerLocationRows.forEach(({ location, depth, parentPath }) => {
      const locationId = String(location?.id || '').trim();
      if (!locationId) return;
      const locationName = String(location?.name || '').trim();
      displayById.set(locationId, {
        depth,
        displayPath: parentPath ? `${parentPath} > ${locationName}` : locationName,
        parentPath
      });
    });
    return displayById;
  }, [activeCustomerLocationRows]);
  const activeCustomerContactRows = useMemo(
    () =>
      activeCustomerContacts.map((contact: any) => {
        const linkedIds: string[] = Array.isArray(contact?.locationLinks)
          ? contact.locationLinks
              .map((link: any) => String(link?.locationId || '').trim())
              .filter((value: string) => value.length > 0)
          : [];
        const explicitIds: string[] = Array.isArray(contact?.locationIds)
          ? contact.locationIds
              .map((value: unknown) => String(value || '').trim())
              .filter((value: string) => value.length > 0)
          : [];
        const rawIds = linkedIds.length
          ? linkedIds
          : (explicitIds.length ? explicitIds : [contact?.locationId]);
        const normalizedIds = rawIds
          .map((value: unknown) => String(value || '').trim())
          .filter((value: string) => value.length > 0);
        const directLocationIds = Array.from(
          new Set<string>(normalizedIds)
        );
        const locationLabels = directLocationIds.map((locationId: string) => {
          const locationDisplay = activeCustomerLocationDisplayById.get(locationId);
          return locationDisplay?.displayPath || 'Assigned location';
        });
        return {
          contact,
          rowKey: String(contact?.id || contact?.email || contact?.fullName || 'contact'),
          hasDirectLocationCoverage: locationLabels.length > 0,
          locationLabels: locationLabels.length ? locationLabels : ['No direct location coverage']
        };
      }),
    [activeCustomerContacts, activeCustomerLocationDisplayById]
  );
  const relocateCurrentLocationId = String(activeRelocateDevice?.locationId || '').trim();
  const relocateLocationOptions = useMemo(() => {
    return activeCustomerLocations.filter((location: any) => {
      if (location?.isActive === false) return false;
      return String(location?.id || '').trim() !== relocateCurrentLocationId;
    });
  }, [activeCustomerLocations, relocateCurrentLocationId]);

  const activePrimaryContact = useMemo(() => {
    return (
      (activeCustomer?.contacts || []).find((contact: any) => contact.isPrimary) ||
      (activeCustomer?.contacts || []).find((contact: any) => contact.isActive) ||
      (activeCustomer?.contacts || [])[0] ||
      null
    );
  }, [activeCustomer]);

  useEffect(() => {
    if (!showCustomerDeviceSlaEditor || !canAccessSla) return;
    if (availableSlaTemplates.length > 0 || loadingSlaTemplates) return;
    void loadAssignableSlaTemplates();
  }, [
    availableSlaTemplates.length,
    canAccessSla,
    loadAssignableSlaTemplates,
    loadingSlaTemplates,
    showCustomerDeviceSlaEditor
  ]);

  const activeCustomerNotes = String(activeCustomer?.notes || '').trim();
  const buildDeviceModelImageSrc = useCallback(
    (url?: string | null) => {
      if (!url) return '';
      if (/^https?:\/\//i.test(url)) return url;
      if (!deviceImageBaseUrl) return url;
      const normalised = String(url).replace(/\\\\/g, '/').replace(/\\/g, '/');
      const trimmed = normalised.startsWith('/') ? normalised.slice(1) : normalised;
      const prefix = deviceImageBaseUrl.endsWith('/') ? deviceImageBaseUrl : `${deviceImageBaseUrl}/`;
      return `${prefix}${trimmed}`;
    },
    [deviceImageBaseUrl]
  );
  const buildCustomerImageSrc = useCallback(
    (url?: string | null) => {
      if (!url) return '';
      if (/^https?:\/\//i.test(url)) return url;
      if (!deviceImageBaseUrl) return url;
      const normalised = String(url).replace(/\\\\/g, '/').replace(/\\/g, '/');
      const trimmed = normalised.startsWith('/') ? normalised.slice(1) : normalised;
      const prefix = deviceImageBaseUrl.endsWith('/') ? deviceImageBaseUrl : `${deviceImageBaseUrl}/`;
      return `${prefix}${trimmed}`;
    },
    [deviceImageBaseUrl]
  );
  const activeEditingContact = useMemo(() => {
    const editingContactId = String(contactForm?.id || '').trim();
    if (!editingContactId) return null;
    return (
      activeCustomerContacts.find(
        (contact: any) => String(contact?.id || '').trim() === editingContactId
      ) || null
    );
  }, [activeCustomerContacts, contactForm?.id]);

  const openCustomerQuickView = useCallback(
    (customer: Customer) => {
      const customerId = String(customer.id || '').trim();
      if (!customerId) return;
      setCustomerQuickViewId(customerId);
      setShowCustomerNotes(false);
      setEditingCustomerNotes(false);
      setCustomerNotesDraft(String(customer?.notes || '').trim());
      setShowCustomerContacts(false);
      setShowCustomerContactEditor(false);
      setShowCustomerLocations(false);
      setShowCustomerLocationEditor(false);
      setActiveCustomerDeviceModelKey(null);
      setShowCustomerAddDevice(false);
      setShowCustomerRelocateDevice(false);
      setCustomerDeviceForm(createEmptyCustomerDeviceForm(false));
      setCustomerDeviceError('');
      setCustomerDeviceEditId(null);
      setCustomerDeviceEditForm(EMPTY_CUSTOMER_DEVICE_EDIT_FORM);
      setCustomerDeviceEditError('');
      setCustomerRelocateDeviceForm({
        deviceId: '',
        action: 'move',
        nextLocationId: '',
        removeTarget: 'inventory'
      });
      setCustomerRelocateDeviceError('');
      setCustomerImagePreviewUrl(null);
      setCustomerImagePreviewName('');
      setCustomerImagePreviewFileState(null);
      setCustomerImageCurrentUrl(String((customer as any)?.imageUrl || '').trim() || null);
      resetLocationForm();
      void loadCustomerDetail(customerId, { force: true }).catch((error: any) => {
        console.warn('Failed to load customer detail:', error);
      });
    },
    [loadCustomerDetail, resetLocationForm]
  );

  const closeCustomerQuickView = useCallback(() => {
    setCustomerQuickViewId(null);
    setShowCustomerNotes(false);
    setEditingCustomerNotes(false);
    setCustomerNotesDraft('');
    setShowCustomerContacts(false);
    setShowCustomerContactEditor(false);
    setShowCustomerLocations(false);
    setShowCustomerLocationEditor(false);
    setActiveCustomerDeviceModelKey(null);
    setShowCustomerAddDevice(false);
    setShowCustomerRelocateDevice(false);
    setCustomerDeviceForm(createEmptyCustomerDeviceForm(false));
    setCustomerDeviceError('');
    setCustomerDeviceEditId(null);
    setCustomerDeviceEditForm(EMPTY_CUSTOMER_DEVICE_EDIT_FORM);
    setCustomerDeviceEditError('');
    setCustomerRelocateDeviceForm({
      deviceId: '',
      action: 'move',
      nextLocationId: '',
      removeTarget: 'inventory'
    });
    setCustomerRelocateDeviceError('');
    setCustomerImagePreviewUrl(null);
    setCustomerImagePreviewName('');
    setCustomerImagePreviewFileState(null);
    setCustomerImageCurrentUrl(null);
    resetLocationForm();
    resetContactForm();
  }, [resetContactForm, resetLocationForm]);

  useEffect(() => {
    const requestedCustomerId = String(customerQuickViewRequest?.customerId || '').trim();
    if (!requestedCustomerId) return;
    const requestedCustomer =
      customers.find(
        (customer: Customer) => String(customer?.id || '').trim() === requestedCustomerId
      ) ||
      customerDetailsById[requestedCustomerId] ||
      ({ id: requestedCustomerId } as Customer);
    openCustomerQuickView(requestedCustomer);
    clearCustomerQuickViewRequest?.();
  }, [
    clearCustomerQuickViewRequest,
    customerDetailsById,
    customerQuickViewRequest,
    customers,
    openCustomerQuickView
  ]);

  useEffect(() => {
    return () => {
      if (customerImagePreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(customerImagePreviewUrl);
      }
    };
  }, [customerImagePreviewUrl]);

  const setCustomerImagePreviewFile = useCallback((file?: File | null) => {
    if (!file || !String(file.type || '').toLowerCase().startsWith('image/')) return;
    setCustomerImagePreviewFileState(file);
    setCustomerImagePreviewUrl(current => {
      if (current?.startsWith('blob:')) {
        URL.revokeObjectURL(current);
      }
      return URL.createObjectURL(file);
    });
    setCustomerImagePreviewName(String(file.name || '').trim());
  }, []);

  const openCustomerCasesWorkspace = useCallback(
    (customerId?: string | null) => {
      const resolvedCustomerId = String(customerId || '').trim();
      if (!resolvedCustomerId) return;
      setCustomerQuickViewId(null);
      setActiveTab('cases');
      setCustomerFilter(resolvedCustomerId);
    },
    [setActiveTab, setCustomerFilter]
  );

  const openCustomerContactEditor = useCallback(
    (contact?: any) => {
      if (!activeCustomer?.id) return;
      const customerId = String(activeCustomer.id || '').trim();
      if (!customerId) return;
      openContactForm(customerId, contact, contact ? contact?.locationId : activeCustomerLocationIds);
      setShowCustomerContactEditor(true);
    },
    [activeCustomer, activeCustomerLocationIds, openContactForm]
  );

  const closeCustomerContactEditor = useCallback(() => {
    resetContactForm();
    setShowCustomerContactEditor(false);
  }, [resetContactForm]);

  const closeCustomerLocationsModal = useCallback(() => {
    setShowCustomerLocations(false);
  }, []);

  const handleSaveCustomerContactEditor = useCallback(async () => {
    const didSave = await handleSubmitContact();
    if (didSave) {
      setShowCustomerContactEditor(false);
    }
  }, [handleSubmitContact]);

  const openCustomerDeviceModelModal = useCallback(
    (model: any) => {
      setActiveCustomerDeviceModelKey(String(model?.key || '').trim() || null);
      const customerId = String(activeCustomer?.id || customerQuickViewId || '').trim();
      if (!customerId) return;
      void loadCustomerDetail(customerId, { force: true }).catch((error: any) => {
        console.warn('Failed to refresh customer device detail:', error);
      });
    },
    [activeCustomer?.id, customerQuickViewId, loadCustomerDetail]
  );

  const closeCustomerDeviceModelModal = useCallback(() => {
    setActiveCustomerDeviceModelKey(null);
    setCustomerDeviceEditId(null);
    setCustomerDeviceEditForm(EMPTY_CUSTOMER_DEVICE_EDIT_FORM);
    setCustomerDeviceEditError('');
  }, []);

  const openCustomerAddDeviceModal = useCallback(() => {
    setCustomerDeviceError('');
    setCustomerDeviceForm(createEmptyCustomerDeviceForm(Boolean(activeCustomer?.assetNumbersEnabled)));
    setShowCustomerAddDevice(true);
  }, [activeCustomer?.assetNumbersEnabled]);

  const closeCustomerAddDeviceModal = useCallback(() => {
    if (savingCustomerDevice) return;
    setShowCustomerAddDevice(false);
    setCustomerDeviceError('');
    setCustomerDeviceForm(createEmptyCustomerDeviceForm(false));
  }, [savingCustomerDevice]);

  const openCustomerRelocateDeviceModal = useCallback(() => {
    setCustomerRelocateDeviceError('');
    setCustomerRelocateDeviceForm({
      deviceId: '',
      action: 'move',
      nextLocationId: '',
      removeTarget: 'inventory'
    });
    setShowCustomerRelocateDevice(true);
  }, []);

  const closeCustomerRelocateDeviceModal = useCallback(() => {
    if (savingCustomerRelocateDevice) return;
    setShowCustomerRelocateDevice(false);
    setCustomerRelocateDeviceError('');
    setCustomerRelocateDeviceForm({
      deviceId: '',
      action: 'move',
      nextLocationId: '',
      removeTarget: 'inventory'
    });
  }, [savingCustomerRelocateDevice]);

  const openCustomerDeviceEditor = useCallback(
    (device: Device) => {
      if (!canManageSerializedDevices) return;
      const deviceId = String(device?.id || '').trim();
      if (!deviceId) return;
      setCustomerDeviceEditId(deviceId);
      setCustomerDeviceEditError('');
      setCustomerDeviceEditForm({
        serialNumber: String(device.serialNumber || '').trim(),
        assetNumber: String(device.assetNumber || '').trim(),
        manufacturer: String(device.manufacturer || device.model?.manufacturer || '').trim(),
        lifecycleStatus: String(device.lifecycleStatus || 'DEPLOYED').trim().toUpperCase() || 'DEPLOYED',
        manufacturedAt: dateInputValueFromIso(device.manufacturedAt),
        installedAt: dateInputValueFromIso(device.installedAt),
        commissionedAt: dateInputValueFromIso(device.commissionedAt),
        warrantyStartAt: dateInputValueFromIso(device.warrantyStartAt),
        warrantyEndAt: dateInputValueFromIso(device.warrantyEndAt)
      });
    },
    [canManageSerializedDevices]
  );

  const closeCustomerDeviceEditor = useCallback(() => {
    if (savingCustomerDeviceEdit) return;
    setCustomerDeviceEditId(null);
    setCustomerDeviceEditForm(EMPTY_CUSTOMER_DEVICE_EDIT_FORM);
    setCustomerDeviceEditError('');
  }, [savingCustomerDeviceEdit]);

  const updateCustomerDeviceEditField = useCallback(
    (field: keyof CustomerDeviceEditFormState, value: string) => {
      setCustomerDeviceEditForm(prev => ({
        ...prev,
        [field]: value
      }));
    },
    []
  );

  const updateCustomerDeviceDateField = useCallback(
    (field: CustomerDeviceDateField, value: string) => {
      setCustomerDeviceError('');
      setCustomerDeviceForm(prev => applyCustomerDeviceDateAutomation(prev, field, value));
    },
    []
  );

  const updateCustomerDeviceEditDateField = useCallback(
    (field: CustomerDeviceDateField, value: string) => {
      setCustomerDeviceEditError('');
      setCustomerDeviceEditForm(prev => applyCustomerDeviceDateAutomation(prev, field, value));
    },
    []
  );

  const openCustomerDeviceSlaModal = useCallback(
    (
      device: any,
      mode: CustomerDeviceSlaEditorMode = 'edit',
      assignmentId: string | null = null
    ) => {
      const deviceId = String(device?.id || '').trim();
      if (!deviceId) return;
      const visibleAssignments = getVisibleDeviceSlaAssignments(device);
      const rawAssignment = assignmentId
        ? visibleAssignments.find(
            (assignment: DeviceSlaAssignment) =>
              String(assignment?.id || '').trim() === String(assignmentId || '').trim()
          ) || null
        : null;
      const currentTemplate = rawAssignment?.slaTemplate || null;
      const currentAssignment = rawAssignment && currentTemplate ? rawAssignment : null;
      setCustomerDeviceSlaDeviceId(deviceId);
      setCustomerDeviceSlaAssignmentId(String(currentAssignment?.id || '').trim() || null);
      setCustomerDeviceSlaEditorMode(currentAssignment && mode === 'view' ? 'view' : 'edit');
      setCustomerDeviceSlaError('');
      setCustomerDeviceSlaForm({
        slaTemplateId:
          String(currentAssignment?.slaTemplateId || currentAssignment?.slaTemplate?.id || '').trim(),
        startDate: dateInputValueFromIso(currentAssignment?.startDate) || getTodayDateInputValue(),
        endDate: dateInputValueFromIso(currentAssignment?.endDate)
      });
      setShowCustomerDeviceSlaEditor(true);
    },
    [getVisibleDeviceSlaAssignments]
  );

  const closeCustomerDeviceSlaModal = useCallback(() => {
    if (savingCustomerDeviceSla) return;
    setShowCustomerDeviceSlaEditor(false);
    setCustomerDeviceSlaEditorMode('edit');
    setCustomerDeviceSlaDeviceId(null);
    setCustomerDeviceSlaAssignmentId(null);
    setCustomerDeviceSlaError('');
    setCustomerDeviceSlaForm({
      slaTemplateId: '',
      startDate: getTodayDateInputValue(),
      endDate: ''
    });
  }, [savingCustomerDeviceSla]);

  const handleOpenCustomerDeviceSlaReport = useCallback(
    async (device: any, assignment?: DeviceSlaAssignment | null) => {
      const deviceId = String(device?.id || '').trim();
      if (!deviceId) return;
      const currentAssignment =
        assignment ||
        (getVisibleDeviceSlaAssignments(device)[0] as DeviceSlaAssignment | undefined) ||
        null;
      const hasSavedReport = Boolean(
        currentAssignment?.slaTemplate?.hasSavedReport ||
          currentAssignment?.slaTemplate?.savedReportAt ||
          currentAssignment?.slaTemplate?.customerReportSavedAt
      );
      if (!hasSavedReport) {
        showInAppAlert('No saved customer-facing SLA report exists for this device yet.', {
          title: 'Agreement unavailable',
          tone: 'primary'
        });
        return;
      }

      setCustomerDeviceSlaReportLoadingId(deviceId);
      try {
        const report = await deviceApi.getSlaReport(deviceId, {
          assignmentId: String(currentAssignment?.id || '').trim() || null
        });
        setCustomerDeviceSlaReport(normalizeCustomerDeviceSlaReport(report));
      } catch (error: any) {
        showInAppAlert(error?.message || 'Failed to load the saved SLA agreement.', {
          title: 'Agreement unavailable',
          tone: 'danger'
        });
      } finally {
        setCustomerDeviceSlaReportLoadingId(current => (current === deviceId ? null : current));
      }
    },
    [getVisibleDeviceSlaAssignments]
  );

  const handleSaveCustomerDeviceSla = useCallback(async () => {
    const deviceId = String(activeCustomerSlaDevice?.id || '').trim();
    const customerId = String(activeCustomer?.id || '').trim();
    const templateId = String(customerDeviceSlaForm.slaTemplateId || '').trim();
    const startDate = String(customerDeviceSlaForm.startDate || '').trim();
    const resolvedTemplate =
      selectedCustomerDeviceSlaTemplate || activeCustomerVisibleSlaAssignment?.slaTemplate || null;
    const endDate =
      String(customerDeviceSlaForm.endDate || '').trim() ||
      getSlaResolvedEndDateInputValue(null, resolvedTemplate, startDate);

    if (!deviceId || !customerId) return;
    if (!templateId) {
      setCustomerDeviceSlaError('Select an SLA template for this device.');
      return;
    }
    if (!startDate) {
      setCustomerDeviceSlaError('Select the SLA start date.');
      return;
    }
    if (!endDate) {
      setCustomerDeviceSlaError('Set the SLA end date, or choose a template period that can resolve one.');
      return;
    }
    if (endDate < startDate) {
      setCustomerDeviceSlaError('SLA end date cannot be earlier than the start date.');
      return;
    }

    const confirmed = await showInAppConfirm(
      activeCustomerVisibleSlaAssignment
        ? 'Changing an assigned SLA affects how this customer device will be handled across service workflows. Save these assignment changes?'
        : 'Assign this SLA to the selected customer device?',
      {
        title: activeCustomerVisibleSlaAssignment ? 'Confirm SLA change' : 'Confirm SLA assignment',
        confirmLabel: activeCustomerVisibleSlaAssignment ? 'Save changes' : 'Assign SLA',
        cancelLabel: 'Cancel',
        tone: activeCustomerVisibleSlaAssignment ? 'danger' : 'primary'
      }
    );
    if (!confirmed) return;

    setSavingCustomerDeviceSla(true);
    setCustomerDeviceSlaError('');
    try {
      await deviceApi.saveSlaAssignment(deviceId, {
        assignmentId: String(activeCustomerVisibleSlaAssignment?.id || '').trim() || undefined,
        slaTemplateId: templateId,
        startDate,
        endDate
      });
      await refreshCustomerState(customerId);
      setShowCustomerDeviceSlaEditor(false);
      setCustomerDeviceSlaEditorMode('edit');
      setCustomerDeviceSlaDeviceId(null);
      setCustomerDeviceSlaAssignmentId(null);
      setCustomerDeviceSlaForm({
        slaTemplateId: '',
        startDate: getTodayDateInputValue(),
        endDate: ''
      });
    } catch (error: any) {
      setCustomerDeviceSlaError(error?.message || 'Failed to save the device SLA assignment.');
    } finally {
      setSavingCustomerDeviceSla(false);
    }
  }, [
    activeCustomer?.id,
    activeCustomerSlaDevice?.id,
    activeCustomerVisibleSlaAssignment,
    customerDeviceSlaForm,
    getSlaResolvedEndDateInputValue,
    refreshCustomerState,
    selectedCustomerDeviceSlaTemplate
  ]);

  const handleEnableCustomerDeviceSlaEdit = useCallback(async () => {
    if (!activeCustomerVisibleSlaAssignment || !canAccessSla || !canManageSerializedDevices) return;
    const confirmed = await showInAppConfirm(
      'Editing a customer device SLA changes contractual handling for future service work. Continue into edit mode?',
      {
        title: 'Unlock SLA assignment editing',
        confirmLabel: 'Edit SLA',
        cancelLabel: 'Cancel',
        tone: 'danger'
      }
    );
    if (!confirmed) return;
    setCustomerDeviceSlaEditorMode('edit');
    setCustomerDeviceSlaError('');
    setCustomerDeviceSlaForm({
      slaTemplateId:
        String(
          activeCustomerVisibleSlaAssignment.slaTemplateId ||
            activeCustomerVisibleSlaAssignment?.slaTemplate?.id ||
            ''
        ).trim(),
      startDate:
        dateInputValueFromIso(activeCustomerVisibleSlaAssignment.startDate) || getTodayDateInputValue(),
      endDate:
        dateInputValueFromIso(activeCustomerVisibleSlaAssignment.endDate) ||
        getSlaResolvedEndDateInputValue(
          activeCustomerVisibleSlaAssignment,
          activeCustomerVisibleSlaAssignment.slaTemplate
        )
    });
  }, [
    activeCustomerVisibleSlaAssignment,
    canAccessSla,
    canManageSerializedDevices,
    getSlaResolvedEndDateInputValue
  ]);

  const handleSaveCustomerDevice = useCallback(async () => {
    if (!activeCustomer?.id) return;
    const modelId = String(customerDeviceForm.modelId || '').trim();
    const serialNumber = String(customerDeviceForm.serialNumber || '').trim();
    const assetNumber = String(customerDeviceForm.assetNumber || '').trim();
    const assetNumbersEnabled = Boolean(customerDeviceForm.assetNumbersEnabled);
    const locationId = String(customerDeviceForm.locationId || '').trim();
    const manufacturedAt = String(customerDeviceForm.manufacturedAt || '').trim();
    const installedAt = String(customerDeviceForm.installedAt || '').trim();
    const commissionedAt = String(customerDeviceForm.commissionedAt || '').trim();
    const warrantyStartAt = String(customerDeviceForm.warrantyStartAt || '').trim();
    const warrantyEndAt = String(customerDeviceForm.warrantyEndAt || '').trim();

    if (!modelId) {
      setCustomerDeviceError('Select an existing device model.');
      return;
    }
    if (!serialNumber) {
      setCustomerDeviceError('Serial number is required.');
      return;
    }
    if (
      assetNumbersEnabled &&
      assetNumber &&
      (activeCustomer.devices || []).some((device: Device) => {
        const existingAsset = String(device?.assetNumber || '').trim().toLowerCase();
        return existingAsset && existingAsset === assetNumber.toLowerCase();
      })
    ) {
      setCustomerDeviceError('A device already exists with that asset number for this customer.');
      return;
    }
    const dateValidationError = validateCustomerDeviceDateSequence(customerDeviceForm);
    if (dateValidationError) {
      setCustomerDeviceError(dateValidationError);
      showInAppAlert(dateValidationError, {
        title: 'Fix device dates',
        tone: 'danger'
      });
      return;
    }

    setSavingCustomerDevice(true);
    setCustomerDeviceError('');
    try {
      if (Boolean(activeCustomer.assetNumbersEnabled) !== assetNumbersEnabled) {
        await customerApi.update(activeCustomer.id, { assetNumbersEnabled });
      }
      await deviceApi.create({
        modelId,
        serialNumber,
        customerId: activeCustomer.id,
        ...(assetNumbersEnabled && assetNumber ? { assetNumber } : {}),
        ...(locationId ? { locationId } : {}),
        ...(manufacturedAt ? { manufacturedAt } : {}),
        ...(installedAt ? { installedAt } : {}),
        ...(commissionedAt ? { commissionedAt } : {}),
        ...(warrantyStartAt ? { warrantyStartAt } : {}),
        ...(warrantyEndAt ? { warrantyEndAt } : {})
      });
      await refreshCustomerState(activeCustomer.id);
      setShowCustomerAddDevice(false);
      setCustomerDeviceForm(createEmptyCustomerDeviceForm(false));
    } catch (error: any) {
      setCustomerDeviceError(error?.message || 'Failed to add device.');
    } finally {
      setSavingCustomerDevice(false);
    }
  }, [activeCustomer, customerDeviceForm, refreshCustomerState]);

  const handleSaveCustomerDeviceEdit = useCallback(async () => {
    const deviceId = String(activeCustomerEditingDevice?.id || '').trim();
    const customerId = String(activeCustomer?.id || '').trim();
    const serialNumber = String(customerDeviceEditForm.serialNumber || '').trim();
    if (!deviceId || !customerId) return;
    if (!serialNumber) {
      setCustomerDeviceEditError('Serial number is required.');
      return;
    }
    const dateValidationError = validateCustomerDeviceDateSequence(customerDeviceEditForm);
    if (dateValidationError) {
      setCustomerDeviceEditError(dateValidationError);
      showInAppAlert(dateValidationError, {
        title: 'Fix device dates',
        tone: 'danger'
      });
      return;
    }

    setSavingCustomerDeviceEdit(true);
    setCustomerDeviceEditError('');
    try {
      await deviceApi.update(deviceId, {
        serialNumber,
        assetNumber: String(customerDeviceEditForm.assetNumber || '').trim(),
        manufacturer: String(customerDeviceEditForm.manufacturer || '').trim(),
        lifecycleStatus: String(customerDeviceEditForm.lifecycleStatus || 'DEPLOYED').trim().toUpperCase(),
        manufacturedAt: customerDeviceEditForm.manufacturedAt || null,
        installedAt: customerDeviceEditForm.installedAt || null,
        commissionedAt: customerDeviceEditForm.commissionedAt || null,
        warrantyStartAt: customerDeviceEditForm.warrantyStartAt || null,
        warrantyEndAt: customerDeviceEditForm.warrantyEndAt || null
      });
      await refreshCustomerState(customerId);
      setCustomerDeviceEditId(null);
      setCustomerDeviceEditForm(EMPTY_CUSTOMER_DEVICE_EDIT_FORM);
    } catch (error: any) {
      setCustomerDeviceEditError(error?.message || 'Failed to save device details.');
    } finally {
      setSavingCustomerDeviceEdit(false);
    }
  }, [activeCustomer?.id, activeCustomerEditingDevice?.id, customerDeviceEditForm, refreshCustomerState]);

  const handleSaveCustomerRelocateDevice = useCallback(async () => {
    if (!activeCustomer?.id) return;
    const deviceId = String(customerRelocateDeviceForm.deviceId || '').trim();
    const action = String(customerRelocateDeviceForm.action || 'move').trim();
    const nextLocationId = String(customerRelocateDeviceForm.nextLocationId || '').trim();
    const removeTarget = String(customerRelocateDeviceForm.removeTarget || 'inventory').trim();

    if (!deviceId) {
      setCustomerRelocateDeviceError('Select a device first.');
      return;
    }

    if (action === 'move') {
      if (!nextLocationId) {
        setCustomerRelocateDeviceError('Select a new location for this device.');
        return;
      }
      if (nextLocationId === relocateCurrentLocationId) {
        setCustomerRelocateDeviceError('Choose a different location.');
        return;
      }
    }

    setSavingCustomerRelocateDevice(true);
    setCustomerRelocateDeviceError('');
    try {
      if (action === 'move') {
        await deviceApi.release(deviceId, {
          targetStatus: 'INVENTORY',
          releaseReason: 'Relocated within customer'
        });
        await deviceApi.assign(deviceId, {
          customerId: activeCustomer.id,
          locationId: nextLocationId
        });
      } else {
        await deviceApi.release(deviceId, {
          targetStatus: removeTarget === 'inactive' ? 'INACTIVE' : 'INVENTORY',
          releaseReason:
            removeTarget === 'inactive'
              ? 'Removed from customer and marked inactive on location'
              : 'Returned from customer to internal inventory'
        });
      }

      await refreshCustomerState(activeCustomer.id);
      setShowCustomerRelocateDevice(false);
      setCustomerRelocateDeviceForm({
        deviceId: '',
        action: 'move',
        nextLocationId: '',
        removeTarget: 'inventory'
      });
    } catch (error: any) {
      setCustomerRelocateDeviceError(error?.message || 'Failed to update device movement.');
    } finally {
      setSavingCustomerRelocateDevice(false);
    }
  }, [activeCustomer, customerRelocateDeviceForm, refreshCustomerState, relocateCurrentLocationId]);

  const openCustomerLocationEditor = useCallback(
    (location?: any) => {
      if (!activeCustomer?.id) return;
      const customerId = String(activeCustomer.id || '').trim();
      if (!customerId) return;
      openLocationForm(customerId, location, location?.parentLocationId);
      setShowCustomerLocationEditor(true);
    },
    [activeCustomer, openLocationForm]
  );

  const closeCustomerLocationEditor = useCallback(() => {
    resetLocationForm();
    setShowCustomerLocationEditor(false);
  }, [resetLocationForm]);

  const handleSaveCustomerLocationEditor = useCallback(async () => {
    const didSave = await handleSubmitLocation();
    if (didSave) {
      setShowCustomerLocationEditor(false);
    }
  }, [handleSubmitLocation]);

  const handleOpenCustomerLocationCreate = useCallback(() => {
    openCustomerLocationEditor();
  }, [openCustomerLocationEditor]);

  const activeEditingLocation = useMemo(() => {
    const editingLocationId = String(locationForm?.id || '').trim();
    if (!editingLocationId) return null;
    return (
      activeCustomerLocations.find(
        (location: any) => String(location?.id || '').trim() === editingLocationId
      ) || null
    );
  }, [activeCustomerLocations, locationForm?.id]);

  const activeLocationOptions = useMemo(() => {
    return activeCustomerLocations.filter((location: any) => {
      const locationId = String(location?.id || '').trim();
      const editingLocationId = String(locationForm?.id || '').trim();
      return locationId && locationId !== editingLocationId;
    });
  }, [activeCustomerLocations, locationForm?.id]);

  const activePrimaryLocationId = String(getPrimaryLocation(activeCustomer)?.id || '').trim();
  const activeDefaultParentLocationId = String(
    (
      activeLocationOptions.find((location: any) => location?.isPrimary) ||
      activeLocationOptions[0] ||
      null
    )?.id || ''
  ).trim();

  const handleMakePrimaryLocation = useCallback(
    async (location: any) => {
      if (!activeCustomer?.id) return;
      await handleSetPrimaryLocation(String(activeCustomer.id || '').trim(), location);
    },
    [activeCustomer, handleSetPrimaryLocation]
  );

  const handleCustomerLocationFilterChange = useCallback(
    (value: string) => {
      setCustomerLocationFilter(value);
      if (value === 'all') return;
      const selectedLocation = customerLocationOptions.find((option: any) => option.value === value);
      if (selectedLocation?.customerId) {
        setCustomerQuickFilter(selectedLocation.customerId);
      }
    },
    [customerLocationOptions, setCustomerLocationFilter, setCustomerQuickFilter]
  );

  const activeIsEditing = Boolean(activeCustomer?.id) && editingCustomerId === activeCustomer?.id;
  const closeAddCustomerModal = useCallback(() => {
    setCustomerImagePreviewUrl(current => {
      if (current?.startsWith('blob:')) {
        URL.revokeObjectURL(current);
      }
      return null;
    });
    setCustomerImagePreviewName('');
    setCustomerImagePreviewFileState(null);
    setCustomerImageCurrentUrl(null);
    resetNewCustomerForm();
    setShowAddCustomerForm(false);
  }, [resetNewCustomerForm, setShowAddCustomerForm]);

  const handleSaveNewCustomer = useCallback(async () => {
    const didSave = await handleCreateCustomer({
      imageFile: customerImagePreviewFile,
      imageUrl: customerImageCurrentUrl
    });
    if (didSave) {
      setCustomerImagePreviewUrl(current => {
        if (current?.startsWith('blob:')) {
          URL.revokeObjectURL(current);
        }
        return null;
      });
      setCustomerImagePreviewName('');
      setCustomerImagePreviewFileState(null);
      setCustomerImageCurrentUrl(null);
    }
  }, [customerImageCurrentUrl, customerImagePreviewFile, handleCreateCustomer]);

  useEffect(() => {
    if (!activeCustomer) return;
    setCustomerNotesDraft(String(activeCustomer.notes || '').trim());
  }, [activeCustomer]);

  return (
    <>
      <div style={styles.header}>
        <div style={styles.casesHeaderPanel}>
          <div style={styles.casesHeaderTop}>
            <div style={styles.casesTitleWrap}>
              <h2 style={styles.pageTitle}>Customers</h2>
              {!customersTopPanelCollapsed && (
                <div style={styles.pageSubtitle}>
                  Manage customer records, linked devices, and service history from one admin
                  workspace.
                </div>
              )}
            </div>
            <div style={styles.casesHeaderActions}>
              <button
                type="button"
                style={styles.casesFilterToggleButton}
                onClick={() => setCustomersTopPanelCollapsed(current => !current)}
              >
                {customersTopPanelCollapsed ? 'Show Top Panel' : 'Hide Top Panel'}
              </button>
              {canManageCustomers && (
                <button
                  style={styles.newCaseButton}
                  onClick={() => {
                    setCustomerImagePreviewUrl(current => {
                      if (current?.startsWith('blob:')) {
                        URL.revokeObjectURL(current);
                      }
                      return null;
                    });
                    setCustomerImagePreviewName('');
                    setCustomerImagePreviewFileState(null);
                    setCustomerImageCurrentUrl(null);
                    resetNewCustomerForm();
                    setShowAddCustomerForm(true);
                  }}
                >
                  + Add Customer
                </button>
              )}
            </div>
          </div>

          {customersTopPanelCollapsed ? (
            <div style={styles.casesTopPanelCollapsedBar}>
              <div style={styles.casesTopPanelCollapsedText}>
                {hasActiveCustomerFilters
                  ? 'Customer filters are active. Expand the top panel to adjust search, sorting, and location history filters.'
                  : 'Top panel hidden. Expand it to search customers, change filters, or review summary cards.'}
              </div>
              {hasActiveCustomerFilters && (
                <button
                  type="button"
                  style={styles.caseFilterActionButton}
                  onClick={clearCustomerFilters}
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <>
              <input
                type="text"
                placeholder="Search customers, contacts, phone, or locations"
                style={styles.searchInput}
                value={customerSearch}
                onChange={event => setCustomerSearch(event.target.value)}
              />

              <div style={styles.casesFilterPanels}>
                <div style={styles.casesFilterPanel}>
                  <div style={styles.casesFilterPanelHeader}>
                    <div style={styles.casesFilterPanelTitle}>Queue Filters</div>
                    <div style={styles.casesFilterPanelHint}>
                      Status, sort order, and clearable directory filters.
                    </div>
                  </div>
                  <div style={styles.casesFilterGrid}>
                    <label style={styles.filterLabel}>
                      <span style={styles.filterLabelText}>Status</span>
                      <select
                        style={styles.filterSelect}
                        value={customerStatusFilter}
                        onChange={event =>
                          setCustomerStatusFilter(event.target.value as CustomerStatusFilter)
                        }
                      >
                        <option value="all">All statuses</option>
                        <option value="active">Active only</option>
                        <option value="inactive">Inactive only</option>
                      </select>
                    </label>

                    <label style={styles.filterLabel}>
                      <span style={styles.filterLabelText}>Sort</span>
                      <select
                        style={styles.filterSelect}
                        value={customerSort}
                        onChange={event => setCustomerSort(event.target.value as CustomerSort)}
                      >
                        {customerSortOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <div style={styles.caseFilterActionRow}>
                      <button
                        type="button"
                        style={{
                          ...styles.caseFilterActionButton,
                          ...(hasActiveCustomerFilters ? {} : styles.caseFilterActionButtonDisabled)
                        }}
                        disabled={!hasActiveCustomerFilters}
                        onClick={clearCustomerFilters}
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>

                <div style={styles.casesFilterPanel}>
                  <div style={styles.casesFilterPanelHeader}>
                    <div style={styles.casesFilterPanelTitle}>Customer And Location History</div>
                    <div style={styles.casesFilterPanelHint}>
                      Search or narrow by customer and location without cluttering the list.
                    </div>
                  </div>
                  <div style={styles.casesSearchableFilterGrid}>
                    <SearchableFilter
                      styles={styles}
                      label="Customer"
                      value={customerQuickFilter}
                      allLabel="All customers"
                      searchPlaceholder="Search customers"
                      emptyLabel="No customers found."
                      options={customerQuickOptions}
                      onChange={setCustomerQuickFilter}
                    />
                    <SearchableFilter
                      styles={styles}
                      label="Location"
                      value={customerLocationFilter}
                      allLabel="All locations"
                      searchPlaceholder="Search locations"
                      emptyLabel="No locations found."
                      options={customerLocationOptions}
                      onChange={handleCustomerLocationFilterChange}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {canManageCustomers && showAddCustomerForm && (
        <div style={styles.caseQuickViewOverlay} onClick={closeAddCustomerModal}>
          <div
            style={{ ...styles.caseQuickViewCard, ...styles.customerCreateModalCard }}
            onClick={event => event.stopPropagation()}
          >
            <div style={styles.caseQuickViewHeader}>
              <div style={styles.caseQuickViewHeaderCopy}>
                <h2 style={styles.caseQuickViewTitle}>Add Customer</h2>
                <div style={styles.customerContactEditorSubTitle}>
                  Create the customer, its primary location, and its primary contact.
                </div>
              </div>
              <button
                type="button"
                style={styles.quickViewBoxButton}
                onClick={closeAddCustomerModal}
                disabled={addingCustomer}
              >
                Close
              </button>
            </div>

            <div style={styles.customerContactEditorForm}>
              <div style={styles.customerQuickViewHero}>
                <div style={styles.customerQuickViewIdentity}>
                  <div style={styles.customerQuickViewAvatar}>
                    {customerImagePreviewUrl || buildCustomerImageSrc(customerImageCurrentUrl) ? (
                      <img
                        src={customerImagePreviewUrl || buildCustomerImageSrc(customerImageCurrentUrl)}
                        alt="Customer thumbnail preview"
                        style={styles.customerQuickViewAvatarImage}
                      />
                    ) : (
                      String(newCustomer.companyName || 'CU')
                        .split(/\s+/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((part: string) => part.charAt(0).toUpperCase())
                        .join('') || 'CU'
                    )}
                  </div>
                  <div style={styles.customerQuickViewIdentityCopy}>
                    <div style={styles.casesFilterPanelTitle}>Customer</div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Customer Name *</label>
                      <input
                        style={styles.inputField}
                        value={newCustomer.companyName}
                        onChange={event =>
                          setNewCustomer((prev: any) => ({ ...prev, companyName: event.target.value }))
                        }
                        placeholder="Acme Corp"
                      />
                    </div>
                    <input
                      ref={customerImageInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={event => {
                        const file = event.target.files?.[0] || null;
                        setCustomerImagePreviewFile(file);
                        event.currentTarget.value = '';
                      }}
                    />
                    <div
                      style={styles.customerImageUploadZone}
                      onDragOver={event => {
                        event.preventDefault();
                      }}
                      onDrop={event => {
                        event.preventDefault();
                        const file = event.dataTransfer.files?.[0] || null;
                        setCustomerImagePreviewFile(file);
                      }}
                    >
                      <div style={styles.customerImageUploadTitle}>
                        {customerImagePreviewUrl || buildCustomerImageSrc(customerImageCurrentUrl)
                          ? 'Customer image ready'
                          : 'Add customer image'}
                      </div>
                      <div style={styles.customerImageUploadMeta}>
                        {customerImagePreviewName || 'Drag and drop an image here, or choose a file.'}
                      </div>
                      <div style={styles.customerQuickViewActionGrid}>
                        <button
                          type="button"
                          style={styles.quickViewBoxButton}
                          onClick={() => customerImageInputRef.current?.click()}
                        >
                          {customerImagePreviewUrl || buildCustomerImageSrc(customerImageCurrentUrl)
                            ? 'Replace Image'
                            : 'Upload Image'}
                        </button>
                        {(customerImagePreviewUrl || buildCustomerImageSrc(customerImageCurrentUrl)) && (
                          <button
                            type="button"
                            style={{ ...styles.quickViewBoxButton, ...styles.quickViewBoxButtonDanger }}
                            onClick={() => {
                              setCustomerImagePreviewUrl(current => {
                                if (current?.startsWith('blob:')) {
                                  URL.revokeObjectURL(current);
                                }
                                return null;
                              });
                              setCustomerImagePreviewName('');
                              setCustomerImagePreviewFileState(null);
                              setCustomerImageCurrentUrl(null);
                            }}
                          >
                            Remove Image
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={styles.detailSection}>
                  <div style={styles.casesFilterPanelTitle}>Notes</div>
                  <div style={styles.customerContactEditorSubTitle}>
                    Internal customer notes can be added now and expanded later.
                  </div>
                  <div style={styles.formField}>
                    <textarea
                      style={{ ...styles.textAreaField, minHeight: '7.5rem' }}
                      value={newCustomer.notes}
                      onChange={event =>
                        setNewCustomer((prev: any) => ({ ...prev, notes: event.target.value }))
                      }
                      placeholder="Internal-only notes about this customer"
                    />
                  </div>
                </div>
              </div>

              <div style={styles.customerQuickViewHero}>
                <div style={styles.detailSection}>
                  <div style={styles.casesFilterPanelTitle}>Primary Location</div>
                  <div style={styles.customerContactEditorSubTitle}>
                    This becomes the customer&apos;s default main site.
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>Address line 1</label>
                      <input
                        style={styles.inputField}
                        value={newCustomer.addressLine1}
                        onChange={event =>
                          setNewCustomer((prev: any) => ({ ...prev, addressLine1: event.target.value }))
                        }
                        placeholder="123 Main St"
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Address line 2</label>
                      <input
                        style={styles.inputField}
                        value={newCustomer.addressLine2}
                        onChange={event =>
                          setNewCustomer((prev: any) => ({ ...prev, addressLine2: event.target.value }))
                        }
                        placeholder="Suite / Unit / Building"
                      />
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>City</label>
                      <input
                        style={styles.inputField}
                        value={newCustomer.city}
                        onChange={event =>
                          setNewCustomer((prev: any) => ({ ...prev, city: event.target.value }))
                        }
                        placeholder="Brisbane"
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>State / Province</label>
                      <input
                        style={styles.inputField}
                        value={newCustomer.stateProvince}
                        onChange={event =>
                          setNewCustomer((prev: any) => ({
                            ...prev,
                            stateProvince: event.target.value
                          }))
                        }
                        placeholder="Queensland"
                      />
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>Postal code</label>
                      <input
                        style={styles.inputField}
                        value={newCustomer.postalCode}
                        onChange={event =>
                          setNewCustomer((prev: any) => ({ ...prev, postalCode: event.target.value }))
                        }
                        placeholder="4000"
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Country</label>
                      <input
                        style={styles.inputField}
                        value={newCustomer.country}
                        onChange={event =>
                          setNewCustomer((prev: any) => ({ ...prev, country: event.target.value }))
                        }
                        placeholder="Australia"
                      />
                    </div>
                  </div>
                </div>

                <div style={styles.detailSection}>
                  <div style={styles.casesFilterPanelTitle}>Primary Contact</div>
                  <div style={styles.customerContactEditorSubTitle}>
                    The main person for communication at this customer.
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>Full Name</label>
                      <input
                        style={styles.inputField}
                        value={newCustomer.primaryContactName}
                        onChange={event =>
                          setNewCustomer((prev: any) => ({
                            ...prev,
                            primaryContactName: event.target.value
                          }))
                        }
                        placeholder="Jean Jean"
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Role / Title</label>
                      <input
                        style={styles.inputField}
                        value={newCustomer.primaryContactRole}
                        onChange={event =>
                          setNewCustomer((prev: any) => ({
                            ...prev,
                            primaryContactRole: event.target.value
                          }))
                        }
                        placeholder="Lab Manager"
                      />
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>Email</label>
                      <input
                        style={styles.inputField}
                        value={newCustomer.primaryContactEmail}
                        onChange={event =>
                          setNewCustomer((prev: any) => ({
                            ...prev,
                            primaryContactEmail: event.target.value
                          }))
                        }
                        placeholder="support@example.com"
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Phone</label>
                      <input
                        style={styles.inputField}
                        value={newCustomer.primaryContactPhone}
                        onChange={event =>
                          setNewCustomer((prev: any) => ({
                            ...prev,
                            primaryContactPhone: event.target.value
                          }))
                        }
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.customerContactEditorActions}>
              <div style={styles.customerContactEditorActionGroup}>
                <button
                  type="button"
                  style={{ ...styles.quickViewBoxButton, ...styles.quickViewBoxButtonPrimary }}
                  onClick={() => void handleSaveNewCustomer()}
                  disabled={addingCustomer}
                >
                  {addingCustomer ? 'Saving...' : 'Save Customer'}
                </button>
                <button
                  type="button"
                  style={styles.quickViewBoxButton}
                  onClick={closeAddCustomerModal}
                  disabled={addingCustomer}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={styles.main}>
        {customers.length === 0 ? (
          <div style={styles.emptyState}>
            {canManageCustomers
              ? 'No customers yet. Add one using the button above.'
              : 'No customers found.'}
          </div>
        ) : (
          <>
            {!customersTopPanelCollapsed && (
              <div style={styles.statsGridWrap}>
                <div style={styles.statsGrid}>
                  <div style={{ ...styles.statCard, ...styles.statCardActive }}>
                    <div style={styles.statNumber}>{filteredCustomers.length}</div>
                    <div style={styles.statLabel}>All Customers</div>
                  </div>
                  <div style={styles.statCard}>
                    <div style={styles.statNumber}>{customerStats.active}</div>
                    <div style={styles.statLabel}>Active</div>
                  </div>
                  <div style={styles.statCard}>
                    <div style={styles.statNumber}>{customerStats.withDevices}</div>
                    <div style={styles.statLabel}>With Devices</div>
                  </div>
                  <div style={styles.statCard}>
                    <div style={styles.statNumber}>{customerStats.withCases}</div>
                    <div style={styles.statLabel}>With Cases</div>
                  </div>
                  <div style={styles.statCard}>
                    <div style={styles.statNumber}>{customerStats.inactive}</div>
                    <div style={styles.statLabel}>Inactive</div>
                  </div>
                </div>
              </div>
            )}

            {filteredCustomers.length === 0 ? (
              <div style={styles.emptyState}>No customers match your filters.</div>
            ) : (
              <div style={styles.caseList}>
                <div style={styles.caseListHeaderRow}>
                  <div style={styles.caseListHeaderBlock}>
                    <div style={styles.caseListHeader}>Customers</div>
                    <div style={styles.caseListMeta}>
                      Showing {customerPageStart}-{customerPageEnd} of {filteredCustomers.length}{' '}
                      matching customers
                    </div>
                  </div>
                  <div style={styles.casePaginationControls}>
                    <div style={styles.casePageSizeGroup}>
                      <span style={styles.casePageSizeLabel}>Per page</span>
                      <select
                        style={styles.casePageSizeSelect}
                        value={customerPageSize}
                        onChange={event => {
                          setCustomerPageSize(Number(event.target.value));
                          setCustomerPage(1);
                        }}
                      >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                    {customerPageCount > 1 && (
                      <>
                        <button
                          type="button"
                          style={{
                            ...styles.casePaginationButton,
                            ...(customerPage <= 1 ? styles.casePaginationButtonDisabled : {})
                          }}
                          disabled={customerPage <= 1}
                          onClick={() => setCustomerPage((current: number) => Math.max(1, current - 1))}
                        >
                          Previous
                        </button>
                        <div style={styles.casePaginationStatus}>
                          Page {customerPage} of {customerPageCount}
                        </div>
                        <button
                          type="button"
                          style={{
                            ...styles.casePaginationButton,
                            ...(customerPage >= customerPageCount
                              ? styles.casePaginationButtonDisabled
                              : {})
                          }}
                          disabled={customerPage >= customerPageCount}
                          onClick={() =>
                            setCustomerPage((current: number) => Math.min(customerPageCount, current + 1))
                          }
                        >
                          Next
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {paginatedCustomers.map((customer: Customer) => {
                  const customerId = String(customer.id || '').trim();
                  const createdLabel = customer.createdAt
                    ? `Added ${new Date(customer.createdAt).toLocaleDateString()}`
                    : 'Created date unavailable';
                  return (
                    <div
                      key={customer.id}
                      style={{
                        ...styles.caseCard,
                        ...(customerQuickViewId === customerId ? styles.caseCardSelected : {})
                      }}
                      onClick={() => openCustomerQuickView(customer)}
                    >
                      <div style={styles.customerCardRow}>
                        <div style={styles.customerCardAvatarWrap}>
                          <div style={styles.customerCardAvatar}>
                            {buildCustomerImageSrc((customer as any)?.imageUrl) ? (
                              <img
                                src={buildCustomerImageSrc((customer as any)?.imageUrl)}
                                alt={`${getCustomerDisplayName(customer)} thumbnail`}
                                style={styles.customerCardAvatarImage}
                              />
                            ) : (
                              getInitials(customer)
                            )}
                          </div>
                        </div>
                        <div style={styles.customerCardPrimaryBlock}>
                          <div style={styles.caseCardTitleRow}>
                            <span style={styles.customerCardTitle}>{getCustomerDisplayName(customer)}</span>
                            <span
                              style={{
                                ...styles.statusBadge,
                                ...(customer.isActive ? {} : styles.statusBadgeInactive)
                              }}
                            >
                              {customer.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div style={styles.customerCardSubTitle}>{getLocationSummary(customer)}</div>
                          <div style={styles.caseCardAttachmentMetaLeft}>{createdLabel}</div>
                        </div>
                        <div style={styles.customerCardSecondaryBlock}>
                          <div style={styles.customerCardCounts}>
                            <span style={styles.customerCountChip}>
                              Devices {getCustomerCount(customer, 'devices')}
                            </span>
                            <span style={styles.customerCountChip}>
                              Cases {getCustomerCount(customer, 'cases')}
                            </span>
                            <span style={styles.customerCountChip}>
                              Contacts {getCustomerCount(customer, 'contacts')}
                            </span>
                            <span style={styles.customerCountChip}>
                              Locations {getCustomerCount(customer, 'locations')}
                            </span>
                          </div>
                        </div>
                        <div style={styles.customerCardTertiaryBlock}>
                          <div style={styles.customerCardViewHint}>Open customer</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {customerQuickViewId && activeCustomer && (
        <div style={styles.caseQuickViewOverlay} onClick={closeCustomerQuickView}>
          <div style={styles.caseQuickViewCard} onClick={event => event.stopPropagation()}>
            <div style={styles.caseQuickViewHeader}>
              <div style={styles.caseQuickViewTitleWrap}>
                <div style={styles.caseQuickViewTitle}>{getCustomerDisplayName(activeCustomer)}</div>
                <div style={styles.caseQuickViewSubTitle}>{getLocationSummary(activeCustomer)}</div>
              </div>
              <button
                type="button"
                style={styles.caseQuickViewCloseButton}
                onClick={closeCustomerQuickView}
              >
                Close
              </button>
            </div>

            <div style={styles.customerQuickViewHero}>
              <div style={styles.customerQuickViewIdentity}>
                <div style={styles.customerQuickViewAvatar}>
                  {buildCustomerImageSrc((activeCustomer as any)?.imageUrl) ? (
                    <img
                      src={buildCustomerImageSrc((activeCustomer as any)?.imageUrl)}
                      alt={`${getCustomerDisplayName(activeCustomer)} thumbnail`}
                      style={styles.customerQuickViewAvatarImage}
                    />
                  ) : (
                    getInitials(activeCustomer)
                  )}
                </div>
                <div style={styles.customerQuickViewIdentityCopy}>
                  <div style={styles.customerQuickViewIdentityTitle}>
                    {getCustomerDisplayName(activeCustomer)}
                  </div>
                  <div style={styles.caseQuickViewSummary}>
                    <span
                      style={{
                        ...styles.statusBadge,
                        ...(activeCustomer.isActive ? {} : styles.statusBadgeInactive)
                      }}
                    >
                      {activeCustomer.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {activeCustomer.createdAt && (
                      <span>
                        Added{' '}
                        {new Date(activeCustomer.createdAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    )}
                    {activeCustomer.isInternal && <span style={styles.customerCountChip}>Internal</span>}
                  </div>
                  <div style={styles.customerQuickViewActionGrid}>
                    {canManageCustomers && !activeIsEditing && (
                      <button
                        type="button"
                        style={styles.quickViewBoxButton}
                        onClick={() => {
                          setCustomerImagePreviewUrl(null);
                          setCustomerImagePreviewName('');
                          setCustomerImagePreviewFileState(null);
                          setCustomerImageCurrentUrl(
                            String((activeCustomer as any)?.imageUrl || '').trim() || null
                          );
                          startEditCustomer(activeCustomer);
                        }}
                      >
                        Edit Customer
                      </button>
                    )}
                    <button
                      type="button"
                      style={styles.quickViewBoxButton}
                      onClick={() => {
                        setCustomerNotesDraft(activeCustomerNotes);
                        setEditingCustomerNotes(false);
                        setShowCustomerNotes(true);
                      }}
                    >
                      View Notes
                    </button>
                  </div>
                </div>
              </div>
              <div style={styles.customerQuickViewCountGrid}>
                <div style={styles.customerQuickViewCountCard}>
                  <div style={styles.customerQuickViewCountNumber}>{activeCustomerCounts.devices}</div>
                  <div style={styles.customerQuickViewCountLabel}>Devices</div>
                </div>
                <div style={styles.customerQuickViewCountCard}>
                  <div style={styles.customerQuickViewCountNumber}>{activeCustomerCounts.cases}</div>
                  <div style={styles.customerQuickViewCountLabel}>Cases</div>
                </div>
                <div style={styles.customerQuickViewCountCard}>
                  <div style={styles.customerQuickViewCountNumber}>{activeCustomerCounts.contacts}</div>
                  <div style={styles.customerQuickViewCountLabel}>Contacts</div>
                </div>
                <div style={styles.customerQuickViewCountCard}>
                  <div style={styles.customerQuickViewCountNumber}>{activeCustomerCounts.locations}</div>
                  <div style={styles.customerQuickViewCountLabel}>Locations</div>
                </div>
              </div>
            </div>

            {activeCustomerLoading && (
              <div style={styles.detailSection}>
                <div style={styles.emptySubtle}>Loading customer details...</div>
              </div>
            )}

            {activeCustomerError && (
              <div style={styles.detailSection}>
                <div style={styles.errorText}>{activeCustomerError}</div>
              </div>
            )}

            {activeIsEditing && (
              <div
                style={styles.caseQuickViewOverlay}
                onClick={() => {
                  handleCancelEditCustomer();
                }}
              >
                <div
                  style={{ ...styles.caseQuickViewCard, ...styles.customerEditModalCard }}
                  onClick={event => event.stopPropagation()}
                >
                  <div style={styles.caseQuickViewHeader}>
                    <div style={styles.caseQuickViewHeaderCopy}>
                      <h2 style={styles.caseQuickViewTitle}>Edit Customer</h2>
                      <div style={styles.caseQuickViewSubtitle}>
                        {getCustomerDisplayName(activeCustomer)}
                      </div>
                    </div>
                    <button
                      type="button"
                      style={styles.quickViewBoxButton}
                      onClick={handleCancelEditCustomer}
                      disabled={savingCustomerId === activeCustomer.id}
                    >
                      Close
                    </button>
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Customer Name *</label>
                    <input
                      style={styles.inputField}
                      value={editCustomer.companyName}
                      onChange={event =>
                        setEditCustomer((prev: any) => ({ ...prev, companyName: event.target.value }))
                      }
                    />
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Thumbnail Image</label>
                    <input
                      ref={customerImageInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={event => {
                        const file = event.target.files?.[0] || null;
                        setCustomerImagePreviewFile(file);
                        event.currentTarget.value = '';
                      }}
                    />
                    <div
                      style={styles.customerImageUploadZone}
                      onDragOver={event => {
                        event.preventDefault();
                      }}
                      onDrop={event => {
                        event.preventDefault();
                        const file = event.dataTransfer.files?.[0] || null;
                        setCustomerImagePreviewFile(file);
                      }}
                    >
                      <div style={styles.customerImageUploadTitle}>
                        {customerImagePreviewUrl || buildCustomerImageSrc(customerImageCurrentUrl)
                          ? 'Customer image ready'
                          : 'Drag and drop an image here'}
                      </div>
                      <div style={styles.customerImageUploadMeta}>
                        {customerImagePreviewName
                          ? customerImagePreviewName
                          : 'Or choose an image file from your device.'}
                      </div>
                      <div style={styles.customerQuickViewActionGrid}>
                        <button
                          type="button"
                          style={styles.quickViewBoxButton}
                          onClick={() => customerImageInputRef.current?.click()}
                        >
                          {customerImagePreviewUrl || buildCustomerImageSrc(customerImageCurrentUrl)
                            ? 'Replace Image'
                            : 'Upload Image'}
                        </button>
                        {(customerImagePreviewUrl || buildCustomerImageSrc(customerImageCurrentUrl)) && (
                          <button
                            type="button"
                            style={{ ...styles.quickViewBoxButton, ...styles.quickViewBoxButtonDanger }}
                            onClick={() => {
                              setCustomerImagePreviewUrl(current => {
                                if (current?.startsWith('blob:')) {
                                  URL.revokeObjectURL(current);
                                }
                                return null;
                              });
                              setCustomerImagePreviewName('');
                              setCustomerImagePreviewFileState(null);
                              setCustomerImageCurrentUrl(null);
                            }}
                          >
                            Remove Image
                          </button>
                        )}
                      </div>
                      <div style={styles.customerImageUploadHint}>
                        Upload a customer image or remove the current one before saving.
                      </div>
                    </div>
                  </div>
                  <div style={styles.actionRow}>
                    <button
                      type="button"
                      style={{ ...styles.button, ...styles.primaryButton }}
                      onClick={() =>
                        handleSaveEditedCustomer({
                          imageFile: customerImagePreviewFile,
                          imageUrl: customerImageCurrentUrl
                        })
                      }
                      disabled={savingCustomerId === activeCustomer.id}
                    >
                      {savingCustomerId === activeCustomer.id ? 'Saving...' : 'Save Customer'}
                    </button>
                    {!activeCustomer.isInternal && (
                      <button
                        type="button"
                        style={{
                          ...styles.quickViewBoxButton,
                          ...(activeCustomer.isActive
                            ? styles.quickViewBoxButtonDanger
                            : styles.quickViewBoxButtonSuccess)
                        }}
                        onClick={() =>
                          handleSetCustomerActive(activeCustomer, !Boolean(activeCustomer.isActive))
                        }
                        disabled={savingCustomerId === activeCustomer.id}
                      >
                        {savingCustomerId === activeCustomer.id
                          ? 'Saving...'
                          : activeCustomer.isActive
                          ? 'Make Inactive'
                          : 'Make Active'}
                      </button>
                    )}
                    <button
                      type="button"
                      style={{ ...styles.button, ...styles.secondaryButton }}
                      onClick={handleCancelEditCustomer}
                      disabled={savingCustomerId === activeCustomer.id}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showCustomerNotes && (
              <div
                style={styles.caseQuickViewOverlay}
                onClick={() => {
                  setShowCustomerNotes(false);
                  setEditingCustomerNotes(false);
                  setCustomerNotesDraft(activeCustomerNotes);
                }}
              >
                <div
                  style={{ ...styles.caseQuickViewCard, ...styles.customerNotesModalCard }}
                  onClick={event => event.stopPropagation()}
                >
                  <div style={styles.caseQuickViewHeader}>
                    <div style={styles.caseQuickViewHeaderCopy}>
                      <h2 style={styles.caseQuickViewTitle}>Customer Notes</h2>
                      <div style={styles.caseQuickViewSubtitle}>
                        {getCustomerDisplayName(activeCustomer)}
                      </div>
                    </div>
                    <div style={styles.customerQuickViewActionGrid}>
                      {canManageCustomers && !editingCustomerNotes && (
                        <button
                          type="button"
                          style={styles.quickViewBoxButton}
                          onClick={() => {
                            setCustomerNotesDraft(activeCustomerNotes);
                            setEditingCustomerNotes(true);
                          }}
                        >
                          {activeCustomerNotes ? 'Edit Notes' : 'Add Notes'}
                        </button>
                      )}
                      {editingCustomerNotes && (
                        <>
                          <button
                            type="button"
                            style={{ ...styles.quickViewBoxButton, ...styles.quickViewBoxButtonSuccess }}
                            disabled={savingCustomerId === activeCustomer.id}
                            onClick={async () => {
                              try {
                                await handleSaveCustomerNotes(activeCustomer, customerNotesDraft);
                                setEditingCustomerNotes(false);
                              } catch {}
                            }}
                          >
                            {savingCustomerId === activeCustomer.id ? 'Saving...' : 'Save Notes'}
                          </button>
                          <button
                            type="button"
                            style={styles.quickViewBoxButton}
                            disabled={savingCustomerId === activeCustomer.id}
                            onClick={() => {
                              setCustomerNotesDraft(activeCustomerNotes);
                              setEditingCustomerNotes(false);
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        style={styles.quickViewBoxButton}
                        onClick={() => {
                          setShowCustomerNotes(false);
                          setEditingCustomerNotes(false);
                          setCustomerNotesDraft(activeCustomerNotes);
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                  <textarea
                    style={{
                      ...styles.customerNotesModalTextarea,
                      ...(editingCustomerNotes
                        ? styles.customerNotesModalTextareaEditable
                        : styles.customerNotesModalTextareaReadOnly)
                    }}
                    value={editingCustomerNotes ? customerNotesDraft : activeCustomerNotes}
                    onChange={event => setCustomerNotesDraft(event.target.value)}
                    readOnly={!editingCustomerNotes}
                    placeholder="No internal notes recorded for this customer yet."
                    rows={16}
                  />
                </div>
              </div>
            )}

            {showCustomerContacts && (
              <div
                style={styles.caseQuickViewOverlay}
                onClick={() => {
                  setShowCustomerContacts(false);
                }}
              >
                <div
                  style={{ ...styles.caseQuickViewCard, ...styles.customerContactsModalCard }}
                  onClick={event => event.stopPropagation()}
                >
                  <div style={styles.caseQuickViewHeader}>
                    <div style={styles.caseQuickViewHeaderCopy}>
                      <h2 style={styles.caseQuickViewTitle}>Contacts</h2>
                      <div style={styles.caseQuickViewSubtitle}>
                        {getCustomerDisplayName(activeCustomer)}
                      </div>
                    </div>
                    <div style={styles.customerQuickViewActionGrid}>
                      {canManageCustomers && (
                        <button
                          type="button"
                          style={styles.quickViewBoxButton}
                          onClick={() => {
                            openCustomerContactEditor();
                          }}
                        >
                          + Add Contact
                        </button>
                      )}
                      <button
                        type="button"
                        style={styles.quickViewBoxButton}
                        onClick={() => {
                          setShowCustomerContacts(false);
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                  {activeCustomerContacts.length === 0 ? (
                    <div style={styles.emptySubtle}>No contacts recorded for this customer yet.</div>
                  ) : (
                    <div style={styles.customerContactsModalList}>
                      {activeCustomerContactRows.map(({ contact, rowKey, locationLabels, hasDirectLocationCoverage }) => (
                        <div
                          key={rowKey}
                          style={{
                            ...styles.customerPrimaryContactCard,
                            ...styles.customerContactPopupSingleRow
                          }}
                        >
                          <div style={styles.customerPrimaryContactCopy}>
                            <div style={styles.customerPrimaryContactEyebrow}>
                              {contact.isPrimary ? 'Primary Contact' : 'Contact'}
                            </div>
                            <div style={styles.customerPrimaryContactName}>{contact.fullName}</div>
                            <div style={styles.customerPrimaryContactRole}>
                              {contact.role || 'Customer contact'}
                            </div>
                          </div>
                          <div style={styles.customerPrimaryContactMetaItem}>
                            <span style={styles.customerPrimaryContactMetaLabel}>Email</span>
                            <span style={styles.customerPrimaryContactMetaValue}>
                              {contact.email || 'Not provided'}
                            </span>
                          </div>
                          <div style={styles.customerPrimaryContactMetaItem}>
                            <span style={styles.customerPrimaryContactMetaLabel}>Phone</span>
                            <span style={styles.customerPrimaryContactMetaValue}>
                              {contact.phone || 'Not provided'}
                            </span>
                          </div>
                          <div style={styles.customerPrimaryContactMetaItem}>
                            <span style={styles.customerPrimaryContactMetaLabel}>Locations</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                              {locationLabels.map((locationLabel: string, locationIndex: number) => (
                                <span
                                  key={`${rowKey}:location:${locationIndex}`}
                                  style={{
                                    ...styles.customerPrimaryContactMetaValue,
                                    color: hasDirectLocationCoverage ? styles.customerPrimaryContactMetaValue.color : '#6b7280',
                                    lineHeight: 1.3,
                                    overflowWrap: 'anywhere'
                                  }}
                                >
                                  {locationLabel}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div
                            style={{
                              ...styles.customerContactPopupActions,
                              ...styles.customerContactHierarchyActions
                            }}
                          >
                            <span
                              style={{
                                ...styles.statusBadge,
                                ...(contact.isActive ? {} : styles.statusBadgeInactive)
                              }}
                            >
                              {contact.isActive ? 'Active' : 'Inactive'}
                            </span>
                            {(contact.portalAccessMode === 'LITE' || contact.portalUser?.portalAccessMode === 'LITE') && (
                              <span style={styles.statusBadge}>Lite portal</span>
                            )}
                            {canManageCustomers && (
                              <button
                                type="button"
                                style={styles.quickViewBoxButton}
                                onClick={() => {
                                  openCustomerContactEditor(contact);
                                }}
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {showCustomerLocations && (
              <div style={styles.caseQuickViewOverlay} onClick={closeCustomerLocationsModal}>
                <div
                  style={{ ...styles.caseQuickViewCard, ...styles.customerContactsModalCard }}
                  onClick={event => event.stopPropagation()}
                >
                  <div style={styles.caseQuickViewHeader}>
                    <div style={styles.caseQuickViewHeaderCopy}>
                      <h2 style={styles.caseQuickViewTitle}>Locations</h2>
                      <div style={styles.caseQuickViewSubtitle}>
                        {getCustomerDisplayName(activeCustomer)}
                      </div>
                    </div>
                    <div style={styles.customerQuickViewActionGrid}>
                      {canManageCustomers && (
                        <button
                          type="button"
                          style={styles.quickViewBoxButton}
                          onClick={handleOpenCustomerLocationCreate}
                        >
                          + Add Location
                        </button>
                      )}
                      <button
                        type="button"
                        style={styles.quickViewBoxButton}
                        onClick={closeCustomerLocationsModal}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                  {activeCustomerLocations.length === 0 ? (
                    <div style={styles.emptySubtle}>No locations recorded for this customer yet.</div>
                  ) : (
                    <div style={styles.customerContactsModalList}>
                      {activeCustomerLocationRows.map(({ location, depth, parentPath }) => (
                        <div
                          key={location.id}
                          style={{
                            ...styles.customerPrimaryContactCard,
                            ...styles.customerLocationPopupSingleRow,
                            borderLeft: depth ? '3px solid #8bb8af' : undefined
                          }}
                        >
                          <div
                            style={{
                              ...styles.customerPrimaryContactCopy,
                              paddingLeft: depth ? Math.min(depth * 24, 72) : 0
                            }}
                          >
                            <div style={styles.customerPrimaryContactEyebrow}>
                              {String(location.id || '').trim() === activePrimaryLocationId
                                ? 'Primary Location'
                                : depth > 0
                                  ? 'Sub-location'
                                  : 'Location'}
                            </div>
                            <div style={styles.customerPrimaryContactName}>{location.name}</div>
                            {depth > 0 && parentPath ? (
                              <div style={styles.customerPrimaryContactRole}>
                                Parent: {parentPath}
                              </div>
                            ) : null}
                            <div style={styles.customerPrimaryContactRole}>
                              {getLocationLine(location)}
                            </div>
                          </div>
                          <div style={styles.customerContactPopupActions}>
                            <span
                              style={{
                                ...styles.statusBadge,
                                ...(location.isActive ? {} : styles.statusBadgeInactive)
                              }}
                            >
                              {location.isActive ? 'Active' : 'Inactive'}
                            </span>
                            {canManageCustomers &&
                              location.isActive !== false &&
                              String(location.id || '').trim() !== activePrimaryLocationId &&
                              String(location.parentLocationId || '').trim() === activePrimaryLocationId && (
                                <button
                                  type="button"
                                  style={styles.quickViewBoxButton}
                                  onClick={() => void handleMakePrimaryLocation(location)}
                                >
                                  Make Primary
                                </button>
                              )}
                            {canManageCustomers && (
                              <button
                                type="button"
                                style={styles.quickViewBoxButton}
                                onClick={() => openCustomerLocationEditor(location)}
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {showCustomerLocationEditor &&
              String(locationFormCustomerId || '').trim() === String(activeCustomer?.id || '').trim() && (
                <div
                  style={styles.caseQuickViewOverlay}
                  onClick={() => {
                    closeCustomerLocationEditor();
                  }}
                >
                  <div
                    style={{ ...styles.caseQuickViewCard, ...styles.customerLocationEditorModalCard }}
                    onClick={event => event.stopPropagation()}
                  >
                    <div style={styles.caseQuickViewHeader}>
                      <div style={styles.caseQuickViewHeaderCopy}>
                        <h2 style={styles.caseQuickViewTitle}>
                          {locationForm.id ? 'Edit Location' : 'Add Location'}
                        </h2>
                        <div style={styles.customerContactEditorSubTitle}>
                          {getCustomerDisplayName(activeCustomer)}
                        </div>
                      </div>
                      <button
                        type="button"
                        style={styles.quickViewBoxButton}
                        onClick={closeCustomerLocationEditor}
                        disabled={savingLocation}
                      >
                        Close
                      </button>
                    </div>

                    <div style={styles.customerContactEditorForm}>
                      <div style={styles.formRow}>
                        <div style={styles.formField}>
                          <label style={styles.label}>Location Name *</label>
                          <input
                            style={styles.inputField}
                            value={locationForm.name}
                            onChange={event =>
                              setLocationForm((prev: any) => ({ ...prev, name: event.target.value }))
                            }
                          />
                        </div>
                        <div style={styles.formField}>
                          <label style={styles.label}>Address line 1</label>
                          <input
                            style={styles.inputField}
                            value={locationForm.addressLine1}
                            onChange={event =>
                              setLocationForm((prev: any) => ({
                                ...prev,
                                addressLine1: event.target.value
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div style={styles.formRow}>
                        <div style={styles.formField}>
                          <label style={styles.label}>Address line 2</label>
                          <input
                            style={styles.inputField}
                            value={locationForm.addressLine2}
                            onChange={event =>
                              setLocationForm((prev: any) => ({
                                ...prev,
                                addressLine2: event.target.value
                              }))
                            }
                          />
                        </div>
                        <div style={styles.formField}>
                          <label style={styles.label}>City / Town</label>
                          <input
                            style={styles.inputField}
                            value={locationForm.city}
                            onChange={event =>
                              setLocationForm((prev: any) => ({ ...prev, city: event.target.value }))
                            }
                          />
                        </div>
                      </div>
                      <div style={styles.formRow}>
                        <div style={styles.formField}>
                          <label style={styles.label}>State / Province</label>
                          <input
                            style={styles.inputField}
                            value={locationForm.stateProvince}
                            onChange={event =>
                              setLocationForm((prev: any) => ({
                                ...prev,
                                stateProvince: event.target.value
                              }))
                            }
                          />
                        </div>
                        <div style={styles.formField}>
                          <label style={styles.label}>Postal code</label>
                          <input
                            style={styles.inputField}
                            value={locationForm.postalCode}
                            onChange={event =>
                              setLocationForm((prev: any) => ({
                                ...prev,
                                postalCode: event.target.value
                              }))
                            }
                          />
                        </div>
                        <div style={styles.formField}>
                          <label style={styles.label}>Country</label>
                          <input
                            style={styles.inputField}
                            value={locationForm.country}
                            onChange={event =>
                              setLocationForm((prev: any) => ({
                                ...prev,
                                country: event.target.value
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div style={styles.formRow}>
                        <div style={styles.formField}>
                          <label style={styles.label}>Sub-location of</label>
                          <select
                            style={styles.inputField}
                            value={locationForm.parentLocationId}
                            disabled={Boolean(locationForm.isPrimary)}
                            onChange={event =>
                              setLocationForm((prev: any) => ({
                                ...prev,
                                parentLocationId: event.target.value
                              }))
                            }
                          >
                            {locationForm.isPrimary ? (
                              <option value="">Primary location</option>
                            ) : activeLocationOptions.length === 0 ? (
                              <option value="">No parent location available yet</option>
                            ) : (
                              activeLocationOptions.map((location: any) => (
                                <option key={location.id} value={location.id}>
                                  {location.name}
                                  {location.isPrimary ? ' (Primary)' : ''}
                                  {location.isActive === false ? ' (Inactive)' : ''}
                                </option>
                              ))
                            )}
                          </select>
                          {!locationForm.isPrimary && activeDefaultParentLocationId ? (
                            <div style={styles.customerContactEditorSubTitle}>
                              Select a higher location for this sub-location. Default is Primary.
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <label style={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={Boolean(locationForm.isPrimary)}
                          onChange={event =>
                            setLocationForm((prev: any) => ({
                              ...prev,
                              isPrimary: event.target.checked,
                              parentLocationId: event.target.checked
                                ? ''
                                : prev.parentLocationId || activeDefaultParentLocationId || ''
                            }))
                          }
                        />
                        Primary location
                      </label>
                      {locationForm.id && activeEditingLocation?.isPrimary && (
                        <div style={styles.customerContactEditorSubTitle}>
                          This is the current primary location.
                        </div>
                      )}
                    </div>

                    <div style={styles.customerContactEditorActions}>
                      <div style={styles.customerContactEditorActionGroup}>
                        <button
                          type="button"
                          style={{ ...styles.quickViewBoxButton, ...styles.quickViewBoxButtonPrimary }}
                          onClick={() => void handleSaveCustomerLocationEditor()}
                          disabled={savingLocation}
                        >
                          {savingLocation
                            ? 'Saving...'
                            : locationForm.id
                            ? 'Save Location'
                            : 'Add Location'}
                        </button>
                        <button
                          type="button"
                          style={styles.quickViewBoxButton}
                          onClick={closeCustomerLocationEditor}
                          disabled={savingLocation}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {showCustomerContactEditor &&
              String(contactFormCustomerId || '').trim() === String(activeCustomer?.id || '').trim() && (
                <div
                  style={styles.caseQuickViewOverlay}
                  onClick={() => {
                    closeCustomerContactEditor();
                  }}
                >
                  <div
                    style={{ ...styles.caseQuickViewCard, ...styles.customerContactEditorModalCard }}
                    onClick={event => event.stopPropagation()}
                  >
                    <div style={styles.caseQuickViewHeader}>
                      <div style={styles.caseQuickViewHeaderCopy}>
                        <h2 style={styles.caseQuickViewTitle}>
                          {contactForm.id ? 'Edit Contact' : 'Add Contact'}
                        </h2>
                        <div style={styles.customerContactEditorSubTitle}>
                          {getCustomerDisplayName(activeCustomer)}
                        </div>
                        {contactForm.id && (
                          <div style={styles.caseQuickViewSummary}>
                            <span
                              style={{
                                ...styles.statusBadge,
                                ...((activeEditingContact?.portalUser?.isActive ?? false)
                                  ? {}
                                  : styles.statusBadgeInactive)
                              }}
                            >
                              {(activeEditingContact?.portalUser?.isActive ?? false)
                                ? 'Portal Access Active'
                                : 'Portal Access Inactive'}
                            </span>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        style={styles.quickViewBoxButton}
                        onClick={closeCustomerContactEditor}
                        disabled={savingContact}
                      >
                        Close
                      </button>
                    </div>

                    <div style={styles.customerContactEditorForm}>
                      <div style={styles.formRow}>
                        <div style={styles.formField}>
                          <label style={styles.label}>Full Name *</label>
                          <input
                            style={styles.inputField}
                            value={contactForm.fullName}
                            onChange={event =>
                              setContactForm((prev: any) => ({ ...prev, fullName: event.target.value }))
                            }
                          />
                        </div>
                        <div style={styles.formField}>
                          <label style={styles.label}>Role / Title</label>
                          <input
                            style={styles.inputField}
                            value={contactForm.role}
                            onChange={event =>
                              setContactForm((prev: any) => ({ ...prev, role: event.target.value }))
                            }
                          />
                        </div>
                      </div>
                      <div style={styles.formRow}>
                        <div style={styles.formField}>
                          <label style={styles.label}>Email</label>
                          <input
                            style={styles.inputField}
                            value={contactForm.email}
                            onChange={event =>
                              setContactForm((prev: any) => ({ ...prev, email: event.target.value }))
                            }
                          />
                        </div>
                        <div style={styles.formField}>
                          <label style={styles.label}>Phone</label>
                          <input
                            style={styles.inputField}
                            value={contactForm.phone}
                            onChange={event =>
                              setContactForm((prev: any) => ({ ...prev, phone: event.target.value }))
                            }
                          />
                        </div>
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Direct location coverage</label>
                        <div
                          style={{
                            ...styles.inputField,
                            height: 'auto',
                            minHeight: 42,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            padding: '10px 12px'
                          }}
                        >
                          {activeCustomerLocationRows.length === 0 ? (
                            <span style={{ color: '#667085' }}>No locations available.</span>
                          ) : (
                            <>
                              <label
                                style={{
                                  ...styles.checkboxLabel,
                                  alignItems: 'flex-start',
                                  fontWeight: 700
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={contactFormHasAllLocations}
                                  onChange={event => {
                                    const nextLocationIds = event.target.checked ? activeCustomerLocationIds : [];
                                    setContactForm((prev: any) => ({
                                      ...prev,
                                      locationIds: nextLocationIds,
                                      locationId: nextLocationIds[0] || ''
                                    }));
                                  }}
                                />
                                Company level coverage
                              </label>
                              {activeCustomerLocationRows.map(({ location, depth, parentPath }) => {
                                const locationId = String(location.id || '').trim();
                                const isChecked = contactFormLocationIds.includes(locationId);
                                return (
                                  <label
                                    key={location.id}
                                    style={{
                                      ...styles.checkboxLabel,
                                      marginLeft: Math.min(depth * 18, 72),
                                      alignItems: 'flex-start'
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={event => {
                                        setContactForm((prev: any) => {
                                          const currentLocationIds = Array.isArray(prev.locationIds)
                                            ? prev.locationIds
                                            : (prev.locationId ? [prev.locationId] : []);
                                          const nextLocationIds = event.target.checked
                                            ? Array.from(new Set([...currentLocationIds, locationId]))
                                            : currentLocationIds.filter((id: string) => id !== locationId);
                                          return {
                                            ...prev,
                                            locationIds: nextLocationIds,
                                            locationId: nextLocationIds[0] || ''
                                          };
                                        });
                                      }}
                                    />
                                    <span>
                                      {parentPath ? `${parentPath} > ` : ''}
                                      {location.name}
                                      {location.isActive === false ? ' (Inactive)' : ''}
                                    </span>
                                  </label>
                                );
                              })}
                            </>
                          )}
                          <span style={{ color: '#667085', fontSize: 12 }}>
                            Select company level coverage to include every location, or tick specific locations.
                          </span>
                        </div>
                      </div>
                      <label style={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={Boolean(contactForm.isPrimary)}
                          onChange={event =>
                            setContactForm((prev: any) => ({ ...prev, isPrimary: event.target.checked }))
                          }
                        />
                        Primary contact
                      </label>
                      <label style={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={contactForm.portalAccessMode === 'LITE'}
                          onChange={event =>
                            setContactForm((prev: any) => ({
                              ...prev,
                              portalAccessMode: event.target.checked ? 'LITE' : 'FULL'
                            }))
                          }
                        />
                        Lite portal access
                      </label>
                    </div>

                    <div style={styles.customerContactEditorActions}>
                      <div style={styles.customerContactEditorActionGroup}>
                        <button
                          type="button"
                          style={{ ...styles.quickViewBoxButton, ...styles.quickViewBoxButtonPrimary }}
                          onClick={() => void handleSaveCustomerContactEditor()}
                          disabled={savingContact}
                        >
                          {savingContact
                            ? 'Saving...'
                            : contactForm.id
                            ? 'Save Contact'
                            : 'Add Contact'}
                        </button>
                        <button
                          type="button"
                          style={styles.quickViewBoxButton}
                          onClick={closeCustomerContactEditor}
                          disabled={savingContact}
                        >
                          Cancel
                        </button>
                        {contactForm.id && (
                          <button
                            type="button"
                            style={styles.quickViewBoxButton}
                            onClick={() => {
                              if (!activeEditingContact) return;
                              void handleInvitePortalUser(activeCustomer.id, activeEditingContact, {
                                resetCredentials: true
                              });
                            }}
                            disabled={
                              !activeEditingContact?.email ||
                              portalContactActionKey === `invite:${activeEditingContact?.id || ''}`
                            }
                          >
                            Portal Invite
                          </button>
                        )}
                        {contactForm.id && activeEditingContact?.portalUser && (
                          <button
                            type="button"
                            style={{
                              ...styles.quickViewBoxButton,
                              ...(activeEditingContact.portalUser.isActive
                                ? styles.quickViewBoxButtonDanger
                                : styles.quickViewBoxButtonSuccess)
                            }}
                            onClick={() => {
                              if (!activeEditingContact) return;
                              void handleSetPortalUserActive(
                                activeCustomer.id,
                                activeEditingContact,
                                !activeEditingContact.portalUser?.isActive
                              );
                            }}
                            disabled={portalContactActionKey === `status:${activeEditingContact?.id || ''}`}
                          >
                            {activeEditingContact.portalUser.isActive ? 'Disable Portal' : 'Enable Portal'}
                          </button>
                        )}
                      </div>
                      {contactForm.id && (
                        <div style={styles.customerContactEditorActionGroup}>
                          <button
                            type="button"
                            style={{
                              ...styles.quickViewBoxButton,
                              ...(contactForm.isActive
                                ? styles.quickViewBoxButtonDanger
                                : styles.quickViewBoxButtonSuccess)
                            }}
                            onClick={() =>
                              setContactForm((prev: any) => ({ ...prev, isActive: !Boolean(prev.isActive) }))
                            }
                            disabled={savingContact}
                          >
                            {contactForm.isActive ? 'Make Inactive' : 'Make Active'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {activeCustomerDeviceModel && (
              <div style={styles.caseQuickViewOverlay} onClick={closeCustomerDeviceModelModal}>
                <div
                  style={{ ...styles.caseQuickViewCard, ...styles.customerDevicesModalCard }}
                  onClick={event => event.stopPropagation()}
                >
                  <div style={styles.caseQuickViewHeader}>
                    <div style={styles.caseQuickViewHeaderCopy}>
                      <h2 style={styles.caseQuickViewTitle}>{activeCustomerDeviceModel.label}</h2>
                      <div style={styles.customerContactEditorSubTitle}>
                        {getCustomerDisplayName(activeCustomer)}
                      </div>
                    </div>
                    <button
                      type="button"
                      style={styles.quickViewBoxButton}
                      onClick={closeCustomerDeviceModelModal}
                    >
                      Close
                    </button>
                  </div>
                  <div style={styles.customerDevicesModalList}>
                    {activeCustomerDeviceModel.devices.map((device: any) => {
                      const lifecycleStatus = String(
                        device?.lifecycleStatus || (device?.isActive === false ? 'INACTIVE' : 'DEPLOYED')
                      )
                        .trim()
                        .toUpperCase();
                      const lifecycleLabel =
                        lifecycleStatus === 'INVENTORY'
                          ? 'Inventory'
                          : lifecycleStatus === 'RETIRED'
                          ? 'Retired'
                          : lifecycleStatus === 'INACTIVE'
                          ? 'Inactive'
                          : 'Deployed';
                      const resolvedLocation = activeCustomerLocations.find(
                        (location: any) =>
                          String(location?.id || '').trim() === String(device?.locationId || '').trim()
                      );
                      const locationLabel = resolvedLocation?.name
                        ? String(resolvedLocation.name)
                        : 'Customer level';
                      const countedCases =
                        activeCustomerCaseCountByDeviceId.get(String(device?.id || '').trim()) ?? null;
                      const caseCount = Number(
                        countedCases ?? device?._count?.cases ?? device?.cases?.length ?? 0
                      );
                      const visibleSlaAssignments = getVisibleDeviceSlaAssignments(device);
                      const deviceIdentity = getCustomerDeviceIdentity(device);

                      return (
                        <div
                          key={device.id}
                          style={{
                            ...styles.customerPrimaryContactCard,
                            gap: '0.85rem'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '0.85rem',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between'
                            }}
                          >
                            <div
                              style={{
                                ...styles.customerDevicePopupSingleRow,
                                display: 'grid',
                                gridTemplateColumns: deviceIdentity.secondaryValue
                                  ? 'minmax(180px, 1.1fr) minmax(115px, 0.7fr) minmax(105px, 0.65fr) minmax(145px, 0.9fr) minmax(70px, 0.35fr)'
                                  : styles.customerDevicePopupSingleRow.gridTemplateColumns,
                                flex: '1 1 760px',
                                minWidth: 0
                              }}
                            >
                              <div style={styles.customerPrimaryContactCopy}>
                                <div style={styles.customerPrimaryContactEyebrow}>
                                  {deviceIdentity.primaryLabel}
                                </div>
                                <div style={styles.customerPrimaryContactName}>
                                  {deviceIdentity.primaryValue}
                                </div>
                                <div style={styles.customerPrimaryContactRole}>
                                  {activeCustomerDeviceModel.label}
                                </div>
                              </div>
                              {deviceIdentity.secondaryValue && (
                                <div style={styles.customerPrimaryContactMetaItem}>
                                  <span style={styles.customerPrimaryContactMetaLabel}>
                                    {deviceIdentity.secondaryLabel}
                                  </span>
                                  <span style={styles.customerPrimaryContactMetaValue}>
                                    {deviceIdentity.secondaryValue}
                                  </span>
                                </div>
                              )}
                              <div style={styles.customerPrimaryContactMetaItem}>
                                <span style={styles.customerPrimaryContactMetaLabel}>Status</span>
                                <span style={styles.customerPrimaryContactMetaValue}>{lifecycleLabel}</span>
                              </div>
                              <div style={styles.customerPrimaryContactMetaItem}>
                                <span style={styles.customerPrimaryContactMetaLabel}>Location</span>
                                <span style={styles.customerPrimaryContactMetaValue}>{locationLabel}</span>
                              </div>
                              <div style={styles.customerPrimaryContactMetaItem}>
                                <span style={styles.customerPrimaryContactMetaLabel}>Cases</span>
                                <span style={styles.customerPrimaryContactMetaValue}>{caseCount}</span>
                              </div>
                            </div>
                            <div
                              style={{
                                ...styles.customerContactEditorActionGroup,
                                flex: '0 0 auto',
                                alignSelf: 'center'
                              }}
                            >
                              {canManageSerializedDevices && (
                                <button
                                  type="button"
                                  style={styles.quickViewBoxButton}
                                  onClick={() => openCustomerDeviceEditor(device)}
                                >
                                  Edit device
                                </button>
                              )}
                              {canAccessSla &&
                                canManageSerializedDevices && (
                                <button
                                  type="button"
                                  style={{
                                    ...styles.quickViewBoxButton,
                                    ...styles.quickViewBoxButtonPrimary
                                  }}
                                  onClick={() => openCustomerDeviceSlaModal(device, 'edit')}
                                >
                                  Assign SLA
                                </button>
                              )}
                            </div>
                          </div>
                          {visibleSlaAssignments.length > 0 && (
                            <div
                              style={{
                                borderTop: '1px solid #e2e8f0',
                                paddingTop: '0.7rem',
                                display: 'grid',
                                gap: '0.5rem'
                              }}
                            >
                              {visibleSlaAssignments.map((assignment: DeviceSlaAssignment) => {
                                const template = assignment.slaTemplate || null;
                                const timing = getSlaAssignmentTiming(assignment, template);
                                return (
                                  <div
                                    key={assignment.id}
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns:
                                        'minmax(220px, 1.35fr) repeat(3, minmax(120px, 0.75fr)) auto',
                                      gap: '0.65rem',
                                      alignItems: 'center',
                                      padding: '0.55rem 0.7rem',
                                      borderRadius: '0.75rem',
                                      background: '#fffdf8'
                                    }}
                                  >
                                    <div style={{ minWidth: 0 }}>
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '0.45rem',
                                          flexWrap: 'wrap',
                                          marginBottom: '0.15rem'
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: '0.74rem',
                                            fontWeight: 800,
                                            color: '#9a6700',
                                            letterSpacing: '0.04em',
                                            textTransform: 'uppercase'
                                          }}
                                        >
                                          SLA
                                        </span>
                                        <span
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '0.15rem 0.5rem',
                                            borderRadius: '999px',
                                            border: `1px solid ${timing.border}`,
                                            background: timing.background,
                                            color: timing.tone,
                                            fontSize: '0.72rem',
                                            fontWeight: 800
                                          }}
                                        >
                                          {timing.label}
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '0.9rem',
                                          fontWeight: 700,
                                          color: '#143f43',
                                          lineHeight: 1.35
                                        }}
                                      >
                                        {getSlaDisplayName(template)}
                                      </div>
                                    </div>
                                    <div style={styles.customerPrimaryContactMetaItem}>
                                      <span style={styles.customerPrimaryContactMetaLabel}>Start</span>
                                      <span style={styles.customerPrimaryContactMetaValue}>
                                        {formatDisplayDate(assignment.startDate)}
                                      </span>
                                    </div>
                                    <div style={styles.customerPrimaryContactMetaItem}>
                                      <span style={styles.customerPrimaryContactMetaLabel}>End</span>
                                      <span style={styles.customerPrimaryContactMetaValue}>
                                        {formatDisplayDate(
                                          getSlaResolvedEndDateInputValue(assignment, template)
                                        )}
                                      </span>
                                    </div>
                                    <div style={styles.customerPrimaryContactMetaItem}>
                                      <span style={styles.customerPrimaryContactMetaLabel}>Period</span>
                                      <span style={styles.customerPrimaryContactMetaValue}>
                                        {getSlaPeriodLabel(template)}
                                      </span>
                                    </div>
                                    <div style={{ justifySelf: 'end' }}>
                                      <button
                                        type="button"
                                        style={styles.quickViewBoxButton}
                                        onClick={() =>
                                          openCustomerDeviceSlaModal(
                                            device,
                                            'view',
                                            String(assignment.id || '').trim()
                                          )
                                        }
                                      >
                                        View SLA
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeCustomerEditingDevice && (
              <div style={styles.caseQuickViewOverlay}>
                <form
                  style={{
                    ...styles.caseQuickViewCard,
                    ...styles.customerAddDeviceModalCard,
                    width: 'min(900px, 95vw)',
                    maxHeight: 'min(86vh, 900px)',
                    gap: '1rem'
                  }}
                  onClick={event => event.stopPropagation()}
                  onSubmit={event => {
                    event.preventDefault();
                    void handleSaveCustomerDeviceEdit();
                  }}
                >
                  <div style={styles.caseQuickViewHeader}>
                    <div style={styles.caseQuickViewHeaderCopy}>
                      <h2 style={styles.caseQuickViewTitle}>Edit Unique Device</h2>
                      <div style={styles.customerContactEditorSubTitle}>
                        {getCustomerDisplayName(activeCustomer)} |{' '}
                        {String(
                          activeCustomerEditingDevice?.model?.modelNumber ||
                            activeCustomerEditingDevice?.modelNumber ||
                            activeCustomerDeviceModel?.label ||
                            'Unknown model'
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      style={styles.quickViewBoxButton}
                      onClick={closeCustomerDeviceEditor}
                      disabled={savingCustomerDeviceEdit}
                    >
                      Close
                    </button>
                  </div>

                  {customerDeviceEditError ? (
                    <div style={styles.errorText}>{customerDeviceEditError}</div>
                  ) : null}

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '0.75rem'
                    }}
                  >
                    <div style={styles.customerQuickViewCountCard}>
                      <div style={styles.customerQuickViewCountLabel}>Customer</div>
                      <div style={{ color: '#143f43', fontWeight: 800 }}>
                        {getCustomerDisplayName(activeCustomer)}
                      </div>
                    </div>
                    <div style={styles.customerQuickViewCountCard}>
                      <div style={styles.customerQuickViewCountLabel}>Location</div>
                      <div style={{ color: '#143f43', fontWeight: 800 }}>
                        {activeCustomerEditingDevice.location?.name ||
                          activeCustomerLocations.find(
                            (location: any) =>
                              String(location?.id || '').trim() ===
                              String(activeCustomerEditingDevice.locationId || '').trim()
                          )?.name ||
                          'Customer level'}
                      </div>
                    </div>
                    <div style={styles.customerQuickViewCountCard}>
                      <div style={styles.customerQuickViewCountLabel}>Device model</div>
                      <div style={{ color: '#143f43', fontWeight: 800 }}>
                        {activeCustomerEditingDevice.model?.modelNumber ||
                          activeCustomerEditingDevice.modelNumber ||
                          activeCustomerDeviceModel?.label ||
                          'Unknown model'}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: '0.85rem'
                    }}
                  >
                    <label style={styles.modalField}>
                      <span style={styles.modalLabel}>Serial Number *</span>
                      <input
                        type="text"
                        style={styles.modalInput}
                        value={customerDeviceEditForm.serialNumber}
                        onChange={event => updateCustomerDeviceEditField('serialNumber', event.target.value)}
                        disabled={savingCustomerDeviceEdit}
                        required
                      />
                    </label>
                    <label style={styles.modalField}>
                      <span style={styles.modalLabel}>Asset Number</span>
                      <input
                        type="text"
                        style={styles.modalInput}
                        value={customerDeviceEditForm.assetNumber}
                        onChange={event => updateCustomerDeviceEditField('assetNumber', event.target.value)}
                        disabled={savingCustomerDeviceEdit}
                      />
                    </label>
                    <label style={styles.modalField}>
                      <span style={styles.modalLabel}>Manufacturer</span>
                      <input
                        type="text"
                        style={styles.modalInput}
                        value={customerDeviceEditForm.manufacturer}
                        onChange={event => updateCustomerDeviceEditField('manufacturer', event.target.value)}
                        disabled={savingCustomerDeviceEdit}
                      />
                    </label>
                    <label style={styles.modalField}>
                      <span style={styles.modalLabel}>Lifecycle Status</span>
                      <select
                        style={styles.modalSelect}
                        value={customerDeviceEditForm.lifecycleStatus}
                        onChange={event => updateCustomerDeviceEditField('lifecycleStatus', event.target.value)}
                        disabled={savingCustomerDeviceEdit}
                      >
                        {DEVICE_LIFECYCLE_OPTIONS.map(status => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={styles.modalField}>
                      <span style={styles.modalLabel}>Manufactured At</span>
                      <input
                        type="date"
                        style={styles.modalInput}
                        value={customerDeviceEditForm.manufacturedAt}
                        onChange={event => updateCustomerDeviceEditDateField('manufacturedAt', event.target.value)}
                        disabled={savingCustomerDeviceEdit}
                      />
                    </label>
                    <label style={styles.modalField}>
                      <span style={styles.modalLabel}>Installed At</span>
                      <input
                        type="date"
                        style={styles.modalInput}
                        value={customerDeviceEditForm.installedAt}
                        onChange={event => updateCustomerDeviceEditDateField('installedAt', event.target.value)}
                        disabled={savingCustomerDeviceEdit}
                      />
                    </label>
                    <label style={styles.modalField}>
                      <span style={styles.modalLabel}>Commissioned At</span>
                      <input
                        type="date"
                        style={styles.modalInput}
                        value={customerDeviceEditForm.commissionedAt}
                        onChange={event => updateCustomerDeviceEditDateField('commissionedAt', event.target.value)}
                        disabled={savingCustomerDeviceEdit}
                      />
                    </label>
                    <label style={styles.modalField}>
                      <span style={styles.modalLabel}>Warranty Start</span>
                      <input
                        type="date"
                        style={styles.modalInput}
                        value={customerDeviceEditForm.warrantyStartAt}
                        onChange={event => updateCustomerDeviceEditDateField('warrantyStartAt', event.target.value)}
                        disabled={savingCustomerDeviceEdit}
                      />
                    </label>
                    <label style={styles.modalField}>
                      <span style={styles.modalLabel}>Warranty End</span>
                      <input
                        type="date"
                        style={styles.modalInput}
                        value={customerDeviceEditForm.warrantyEndAt}
                        onChange={event => updateCustomerDeviceEditDateField('warrantyEndAt', event.target.value)}
                        disabled={savingCustomerDeviceEdit}
                      />
                    </label>
                  </div>

                  <div style={styles.customerContactEditorActions}>
                    <div />
                    <div style={styles.customerContactEditorActionGroup}>
                      <button
                        type="button"
                        style={styles.quickViewBoxButton}
                        onClick={closeCustomerDeviceEditor}
                        disabled={savingCustomerDeviceEdit}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={{ ...styles.quickViewBoxButton, ...styles.quickViewBoxButtonPrimary }}
                        disabled={savingCustomerDeviceEdit}
                      >
                        {savingCustomerDeviceEdit ? 'Saving...' : 'Save Device'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}

            {showCustomerDeviceSlaEditor && activeCustomerSlaDevice && (
              <div style={styles.caseQuickViewOverlay}>
                <div style={customerDeviceSlaModalCardStyle} onClick={event => event.stopPropagation()}>
                  <div style={styles.caseQuickViewHeader}>
                    <div style={styles.caseQuickViewHeaderCopy}>
                      <h2 style={styles.caseQuickViewTitle}>
                        {activeCustomerVisibleSlaAssignment
                          ? customerDeviceSlaEditorMode === 'view'
                            ? 'View Device SLA'
                            : 'Edit Device SLA'
                          : 'Assign SLA'}
                      </h2>
                      <div style={styles.customerContactEditorSubTitle}>
                        {getCustomerDisplayName(activeCustomer)} |{' '}
                        {String(
                          getCustomerDeviceIdentityLabel(activeCustomerSlaDevice)
                        )}{' '}
                        |{' '}
                        {String(
                          activeCustomerSlaDevice?.model?.modelNumber ||
                            activeCustomerSlaDevice?.modelNumber ||
                            'Unknown model'
                        )}
                      </div>
                    </div>
                    <div style={styles.customerContactEditorActionGroup}>
                      {activeCustomerVisibleSlaAssignment &&
                        Boolean(
                          activeCustomerVisibleSlaAssignment?.slaTemplate?.hasSavedReport ||
                            activeCustomerVisibleSlaAssignment?.slaTemplate?.savedReportAt ||
                            activeCustomerVisibleSlaAssignment?.slaTemplate?.customerReportSavedAt
                        ) && (
                          <button
                            type="button"
                            style={styles.quickViewBoxButton}
                            onClick={() =>
                              void handleOpenCustomerDeviceSlaReport(
                                activeCustomerSlaDevice,
                                activeCustomerVisibleSlaAssignment
                              )
                            }
                            disabled={
                              customerDeviceSlaReportLoadingId ===
                              String(activeCustomerSlaDevice?.id || '').trim()
                            }
                          >
                            {customerDeviceSlaReportLoadingId ===
                            String(activeCustomerSlaDevice?.id || '').trim()
                              ? 'Opening...'
                              : 'View Agreement'}
                          </button>
                        )}
                      {activeCustomerVisibleSlaAssignment &&
                        customerDeviceSlaEditorMode === 'view' &&
                        canAccessSla &&
                        canManageSerializedDevices && (
                          <button
                            type="button"
                            style={styles.quickViewBoxButton}
                            onClick={() => void handleEnableCustomerDeviceSlaEdit()}
                          >
                            Edit SLA
                          </button>
                        )}
                      <button
                        type="button"
                        style={styles.quickViewBoxButton}
                        onClick={closeCustomerDeviceSlaModal}
                        disabled={savingCustomerDeviceSla}
                      >
                        Close
                      </button>
                    </div>
                  </div>

                  <div style={styles.customerDeviceEditorForm}>
                    <div
                      style={{
                        ...styles.customerPrimaryContactCard,
                        gap: '0.8rem'
                      }}
                    >
                      <div style={{ overflowX: 'auto' }}>
                        <div style={customerDeviceSlaSummaryGridStyle}>
                          <div style={styles.customerPrimaryContactMetaItem}>
                            <span style={styles.customerPrimaryContactMetaLabel}>
                              {activeCustomerVisibleSlaAssignment ? 'Current Agreement' : 'Assignment'}
                            </span>
                            <span style={styles.customerPrimaryContactMetaValue}>
                              {activeCustomerVisibleSlaAssignment?.slaTemplate
                                ? getSlaDisplayName(activeCustomerVisibleSlaAssignment.slaTemplate)
                                : 'No SLA assigned'}
                            </span>
                          </div>
                          <div style={styles.customerPrimaryContactMetaItem}>
                            <span style={styles.customerPrimaryContactMetaLabel}>Status</span>
                            <span style={styles.customerPrimaryContactMetaValue}>
                              {activeCustomerVisibleSlaAssignment
                                ? getSlaAssignmentTiming(
                                    activeCustomerVisibleSlaAssignment,
                                    activeCustomerVisibleSlaAssignment.slaTemplate
                                  ).label
                                : 'Not assigned'}
                            </span>
                          </div>
                          <div style={styles.customerPrimaryContactMetaItem}>
                            <span style={styles.customerPrimaryContactMetaLabel}>Period</span>
                            <span style={styles.customerPrimaryContactMetaValue}>
                              {activeCustomerVisibleSlaAssignment?.slaTemplate
                                ? getSlaPeriodLabel(activeCustomerVisibleSlaAssignment.slaTemplate)
                                : 'Not assigned'}
                            </span>
                          </div>
                          <div style={styles.customerPrimaryContactMetaItem}>
                            <span style={styles.customerPrimaryContactMetaLabel}>Start Date</span>
                            <span style={styles.customerPrimaryContactMetaValue}>
                              {activeCustomerVisibleSlaAssignment
                                ? formatDisplayDate(activeCustomerVisibleSlaAssignment.startDate)
                                : 'Not assigned'}
                            </span>
                          </div>
                          <div style={styles.customerPrimaryContactMetaItem}>
                            <span style={styles.customerPrimaryContactMetaLabel}>End Date</span>
                            <span style={styles.customerPrimaryContactMetaValue}>
                              {activeCustomerVisibleSlaAssignment
                                ? formatDisplayDate(
                                    getSlaResolvedEndDateInputValue(
                                      activeCustomerVisibleSlaAssignment,
                                      activeCustomerVisibleSlaAssignment.slaTemplate
                                    )
                                  )
                                : 'Not assigned'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {customerDeviceSlaEditorMode === 'edit' && (
                      <>
                        <div style={styles.formField}>
                          <label style={styles.label}>SLA Template *</label>
                          <select
                            style={styles.inputField}
                            value={customerDeviceSlaForm.slaTemplateId}
                            onChange={event =>
                              setCustomerDeviceSlaForm(prev => ({
                                ...prev,
                                slaTemplateId: event.target.value
                              }))
                            }
                            disabled={loadingSlaTemplates || savingCustomerDeviceSla}
                          >
                            <option value="">
                              {loadingSlaTemplates
                                ? 'Loading available SLA templates...'
                                : eligibleSlaTemplatesForDevice.length > 0
                                ? 'Select an active SLA template'
                                : 'No active SLA templates are available for this device model'}
                            </option>
                            {eligibleSlaTemplatesForDevice.map(template => (
                              <option key={template.id} value={template.id}>
                                {getSlaTemplateOptionLabel(template)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div style={styles.formRow}>
                          <div style={styles.formField}>
                            <label style={styles.label}>Agreement Start Date *</label>
                            <input
                              type="date"
                              style={styles.inputField}
                              value={customerDeviceSlaForm.startDate}
                              onChange={event =>
                                setCustomerDeviceSlaForm(prev => ({
                                  ...prev,
                                  startDate: event.target.value
                                }))
                              }
                              disabled={savingCustomerDeviceSla}
                            />
                          </div>
                          <div style={styles.formField}>
                            <label style={styles.label}>Agreement End Date *</label>
                            <input
                              type="date"
                              style={styles.inputField}
                              value={
                                customerDeviceSlaForm.endDate ||
                                getSlaResolvedEndDateInputValue(
                                  null,
                                  selectedCustomerDeviceSlaTemplate ||
                                    activeCustomerVisibleSlaAssignment?.slaTemplate,
                                  customerDeviceSlaForm.startDate
                                )
                              }
                              onChange={event =>
                                setCustomerDeviceSlaForm(prev => ({
                                  ...prev,
                                  endDate: event.target.value
                                }))
                              }
                              disabled={savingCustomerDeviceSla}
                            />
                          </div>
                        </div>

                        <div style={styles.formField}>
                          <label style={styles.label}>Selected Period</label>
                          <input
                            style={styles.inputField}
                            value={
                              selectedCustomerDeviceSlaTemplate
                                ? getSlaPeriodLabel(selectedCustomerDeviceSlaTemplate)
                                : activeCustomerVisibleSlaAssignment?.slaTemplate
                                ? getSlaPeriodLabel(activeCustomerVisibleSlaAssignment.slaTemplate)
                                : ''
                            }
                            readOnly
                          />
                        </div>

                        {selectedCustomerDeviceSlaTemplate && (
                          <div
                            style={{
                              ...styles.customerPrimaryContactCard,
                              gap: '0.7rem'
                            }}
                          >
                            <div style={styles.customerPrimaryContactEyebrow}>Selected Template</div>
                            <div style={styles.customerPrimaryContactName}>
                              {getSlaDisplayName(selectedCustomerDeviceSlaTemplate)}
                            </div>
                            <div style={styles.customerPrimaryContactRole}>
                              Internal template: {selectedCustomerDeviceSlaTemplate.name}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {customerDeviceSlaError ? <div style={styles.errorText}>{customerDeviceSlaError}</div> : null}
                  </div>

                  <div style={styles.customerContactEditorActions}>
                    <div style={styles.customerContactEditorActionGroup}>
                      {customerDeviceSlaEditorMode === 'edit' && (
                        <button
                          type="button"
                          style={{ ...styles.quickViewBoxButton, ...styles.quickViewBoxButtonPrimary }}
                          onClick={() => void handleSaveCustomerDeviceSla()}
                          disabled={
                            savingCustomerDeviceSla ||
                            loadingSlaTemplates ||
                            eligibleSlaTemplatesForDevice.length === 0
                          }
                        >
                          {savingCustomerDeviceSla ? 'Saving...' : 'Save SLA Assignment'}
                        </button>
                      )}
                      <button
                        type="button"
                        style={styles.quickViewBoxButton}
                        onClick={closeCustomerDeviceSlaModal}
                        disabled={savingCustomerDeviceSla}
                      >
                        {customerDeviceSlaEditorMode === 'edit' ? 'Cancel' : 'Close'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {customerDeviceSlaReport && (
              <div style={styles.caseQuickViewOverlay}>
                <div
                  style={{
                    ...styles.caseQuickViewCard,
                    width: 'min(1180px, 96vw)',
                    maxHeight: 'min(88vh, 960px)',
                    gap: '1rem'
                  }}
                  onClick={event => event.stopPropagation()}
                >
                  <div style={styles.caseQuickViewHeader}>
                    <div style={styles.caseQuickViewHeaderCopy}>
                      <h2 style={styles.caseQuickViewTitle}>{customerDeviceSlaReport.title}</h2>
                      <div style={styles.customerContactEditorSubTitle}>
                        {customerDeviceSlaReport.subtitle || 'Customer-facing SLA agreement'}
                      </div>
                    </div>
                    <button
                      type="button"
                      style={styles.quickViewBoxButton}
                      onClick={() => setCustomerDeviceSlaReport(null)}
                    >
                      Close
                    </button>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gap: '1rem',
                      overflowY: 'auto',
                      paddingRight: '0.2rem'
                    }}
                  >
                    <section
                      style={{
                        border: '1px solid #d7c6a3',
                        borderRadius: '0.9rem',
                        background: '#fffdf8',
                        padding: '1rem',
                        display: 'grid',
                        gap: '0.85rem'
                      }}
                    >
                      <div
                        style={{
                          fontSize: '1.02rem',
                          lineHeight: 1.7,
                          color: '#143f43'
                        }}
                      >
                        {customerDeviceSlaReport.overview}
                      </div>
                      {customerDeviceSlaReport.savedAt ? (
                        <div style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
                          Saved {formatDisplayDate(customerDeviceSlaReport.savedAt)}
                        </div>
                      ) : null}
                    </section>

                    {customerDeviceSlaReport.sections.map((section, sectionIndex) => (
                      <section
                        key={`${section.title}-${sectionIndex}`}
                        style={{
                          border: '1px solid #d7c6a3',
                          borderRadius: '0.9rem',
                          background: '#ffffff',
                          padding: '1rem',
                          display: 'grid',
                          gap: '0.75rem'
                        }}
                      >
                        <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#143f43' }}>
                          {section.title}
                        </div>
                        <div style={{ color: '#475569', lineHeight: 1.6 }}>{section.summary}</div>
                        {section.includedItems.length > 0 ? (
                          <div style={{ display: 'grid', gap: '0.45rem' }}>
                            {section.includedItems.map(item => (
                              <div key={`${section.title}-${item}`} style={{ color: '#143f43', lineHeight: 1.5 }}>
                                - {item}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </section>
                    ))}

                    {customerDeviceSlaReport.appendices.length > 0 && (
                      <section
                        style={{
                          border: '1px solid #d7c6a3',
                          borderRadius: '0.9rem',
                          background: '#ffffff',
                          padding: '1rem',
                          display: 'grid',
                          gap: '0.9rem'
                        }}
                      >
                        <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#143f43' }}>
                          Appendices
                        </div>
                        {customerDeviceSlaReport.appendices.map(appendix => (
                          <div
                            key={appendix.key}
                            style={{
                              border: '1px solid #e2e8f0',
                              borderRadius: '0.85rem',
                              padding: '0.9rem',
                              display: 'grid',
                              gap: '0.7rem'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                              <div style={{ fontWeight: 800, color: '#143f43' }}>{appendix.title}</div>
                              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>
                                {appendix.kind === 'schedule' ? 'Schedule Appendix' : 'List Appendix'}
                              </div>
                            </div>
                            <div style={{ color: '#475569', lineHeight: 1.55 }}>{appendix.summary}</div>
                            {appendix.columns.length > 0 ? (
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr>
                                      {appendix.columns.map(column => (
                                        <th
                                          key={column}
                                          style={{
                                            textAlign: 'left',
                                            padding: '0.7rem 0.75rem',
                                            borderBottom: '1px solid #d7c6a3',
                                            color: '#143f43',
                                            fontSize: '0.8rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em'
                                          }}
                                        >
                                          {column}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {appendix.rows.map((row, rowIndex) => (
                                      <tr key={`${appendix.key}-${rowIndex}`}>
                                        {appendix.columns.map((column, columnIndex) => (
                                          <td
                                            key={`${appendix.key}-${column}-${rowIndex}`}
                                            style={{
                                              padding: '0.7rem 0.75rem',
                                              borderBottom: '1px solid #f1f5f9',
                                              color: '#1f2937',
                                              verticalAlign: 'top'
                                            }}
                                          >
                                            {row[columnIndex] || '--'}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </section>
                    )}
                  </div>
                </div>
              </div>
            )}

            {showCustomerAddDevice && (
              <div style={styles.caseQuickViewOverlay} onClick={closeCustomerAddDeviceModal}>
                <div
                  style={{ ...styles.caseQuickViewCard, ...styles.customerAddDeviceModalCard }}
                  onClick={event => event.stopPropagation()}
                >
                  <div style={styles.caseQuickViewHeader}>
                    <div style={styles.caseQuickViewHeaderCopy}>
                      <h2 style={styles.caseQuickViewTitle}>Add Device</h2>
                      <div style={styles.customerContactEditorSubTitle}>
                        {getCustomerDisplayName(activeCustomer)}
                      </div>
                    </div>
                    <button
                      type="button"
                      style={styles.quickViewBoxButton}
                      onClick={closeCustomerAddDeviceModal}
                      disabled={savingCustomerDevice}
                    >
                      Close
                    </button>
                  </div>

                  <div style={styles.customerDeviceEditorForm}>
                    <div style={styles.formRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Device Model *</label>
                        <select
                          style={styles.inputField}
                          value={customerDeviceForm.modelId}
                          onChange={event =>
                            setCustomerDeviceForm(prev => ({ ...prev, modelId: event.target.value }))
                          }
                        >
                          <option value="">Select an existing device model</option>
                          {availableDeviceModels.map((model: any) => (
                            <option key={model.id} value={model.id}>
                              {String(model?.modelNumber || model?.modelName || 'Unknown model')}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Serial Number *</label>
                        <input
                          style={styles.inputField}
                          value={customerDeviceForm.serialNumber}
                          onChange={event =>
                            setCustomerDeviceForm(prev => ({ ...prev, serialNumber: event.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <label style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={customerDeviceForm.assetNumbersEnabled}
                        onChange={event =>
                          setCustomerDeviceForm(prev => ({
                            ...prev,
                            assetNumbersEnabled: event.target.checked,
                            assetNumber: event.target.checked ? prev.assetNumber : ''
                          }))
                        }
                      />
                      Customer uses asset numbers
                    </label>
                    {customerDeviceForm.assetNumbersEnabled && (
                      <div style={styles.formField}>
                        <label style={styles.label}>Asset Number</label>
                        <input
                          style={styles.inputField}
                          value={customerDeviceForm.assetNumber}
                          onChange={event =>
                            setCustomerDeviceForm(prev => ({ ...prev, assetNumber: event.target.value }))
                          }
                        />
                      </div>
                    )}
                    <div style={styles.formField}>
                      <label style={styles.label}>Location</label>
                      <select
                        style={styles.inputField}
                        value={customerDeviceForm.locationId}
                        onChange={event =>
                          setCustomerDeviceForm(prev => ({ ...prev, locationId: event.target.value }))
                        }
                      >
                        <option value="">Primary location / customer level</option>
                        {activeCustomerLocations
                          .filter((location: any) => location?.isActive !== false)
                          .map((location: any) => (
                            <option key={location.id} value={location.id}>
                              {location.name}
                            </option>
                        ))}
                      </select>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Manufactured At</label>
                        <input
                          type="date"
                          style={styles.inputField}
                          value={customerDeviceForm.manufacturedAt}
                          onChange={event => updateCustomerDeviceDateField('manufacturedAt', event.target.value)}
                        />
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Installed At</label>
                        <input
                          type="date"
                          style={styles.inputField}
                          value={customerDeviceForm.installedAt}
                          onChange={event => updateCustomerDeviceDateField('installedAt', event.target.value)}
                        />
                      </div>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Commissioned At</label>
                        <input
                          type="date"
                          style={styles.inputField}
                          value={customerDeviceForm.commissionedAt}
                          onChange={event => updateCustomerDeviceDateField('commissionedAt', event.target.value)}
                        />
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Warranty Start</label>
                        <input
                          type="date"
                          style={styles.inputField}
                          value={customerDeviceForm.warrantyStartAt}
                          onChange={event => updateCustomerDeviceDateField('warrantyStartAt', event.target.value)}
                        />
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Warranty End</label>
                        <input
                          type="date"
                          style={styles.inputField}
                          value={customerDeviceForm.warrantyEndAt}
                          onChange={event => updateCustomerDeviceDateField('warrantyEndAt', event.target.value)}
                        />
                      </div>
                    </div>
                    {customerDeviceError ? <div style={styles.errorText}>{customerDeviceError}</div> : null}
                  </div>

                  <div style={styles.customerContactEditorActions}>
                    <div style={styles.customerContactEditorActionGroup}>
                      <button
                        type="button"
                        style={{ ...styles.quickViewBoxButton, ...styles.quickViewBoxButtonPrimary }}
                        onClick={() => void handleSaveCustomerDevice()}
                        disabled={savingCustomerDevice}
                      >
                        {savingCustomerDevice ? 'Saving...' : 'Save Device'}
                      </button>
                      <button
                        type="button"
                        style={styles.quickViewBoxButton}
                        onClick={closeCustomerAddDeviceModal}
                        disabled={savingCustomerDevice}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showCustomerRelocateDevice && (
              <div style={styles.caseQuickViewOverlay} onClick={closeCustomerRelocateDeviceModal}>
                <div
                  style={{ ...styles.caseQuickViewCard, ...styles.customerAddDeviceModalCard }}
                  onClick={event => event.stopPropagation()}
                >
                  <div style={styles.caseQuickViewHeader}>
                    <div style={styles.caseQuickViewHeaderCopy}>
                      <h2 style={styles.caseQuickViewTitle}>Relocate Device</h2>
                      <div style={styles.customerContactEditorSubTitle}>
                        {getCustomerDisplayName(activeCustomer)}
                      </div>
                    </div>
                    <button
                      type="button"
                      style={styles.quickViewBoxButton}
                      onClick={closeCustomerRelocateDeviceModal}
                      disabled={savingCustomerRelocateDevice}
                    >
                      Close
                    </button>
                  </div>

                  <div style={styles.customerDeviceEditorForm}>
                    <div style={styles.formRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Assigned Device *</label>
                        <select
                          style={styles.inputField}
                          value={customerRelocateDeviceForm.deviceId}
                          onChange={event =>
                            setCustomerRelocateDeviceForm(prev => ({
                              ...prev,
                              deviceId: event.target.value,
                              nextLocationId: ''
                            }))
                          }
                        >
                          <option value="">Select a customer device</option>
                          {activeCustomerAssignedDevices.map((device: any) => {
                            const modelLabel = String(
                              device?.model?.modelNumber || device?.modelNumber || 'Unknown model'
                            ).trim();
                            const serialLabel = getCustomerDeviceIdentityLabel(device);
                            return (
                              <option key={device.id} value={device.id}>
                                {serialLabel} | {modelLabel}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Action *</label>
                        <select
                          style={styles.inputField}
                          value={customerRelocateDeviceForm.action}
                          onChange={event =>
                            setCustomerRelocateDeviceForm(prev => ({
                              ...prev,
                              action: event.target.value,
                              nextLocationId: '',
                              removeTarget: prev.removeTarget || 'inventory'
                            }))
                          }
                        >
                          <option value="move">Move to a new location</option>
                          <option value="remove">Remove device</option>
                        </select>
                      </div>
                    </div>

                    <div style={styles.formField}>
                      <label style={styles.label}>Current Location</label>
                      <input
                        style={styles.inputField}
                        value={
                          activeRelocateDevice?.location?.name
                            ? String(activeRelocateDevice.location.name)
                            : 'Primary location / customer level'
                        }
                        readOnly
                      />
                    </div>

                    {customerRelocateDeviceForm.action === 'move' ? (
                      <div style={styles.formField}>
                        <label style={styles.label}>New Location *</label>
                        <select
                          style={styles.inputField}
                          value={customerRelocateDeviceForm.nextLocationId}
                          onChange={event =>
                            setCustomerRelocateDeviceForm(prev => ({
                              ...prev,
                              nextLocationId: event.target.value
                            }))
                          }
                        >
                          <option value="">
                            {relocateLocationOptions.length > 0
                              ? 'Select a different customer location'
                              : 'No alternative active locations available'}
                          </option>
                          {relocateLocationOptions.map((location: any) => (
                            <option key={location.id} value={location.id}>
                              {location.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div style={styles.formField}>
                        <label style={styles.label}>Remove To *</label>
                        <select
                          style={styles.inputField}
                          value={customerRelocateDeviceForm.removeTarget}
                          onChange={event =>
                            setCustomerRelocateDeviceForm(prev => ({
                              ...prev,
                              removeTarget: event.target.value
                            }))
                          }
                        >
                          <option value="inventory">Return to Internal</option>
                          <option value="inactive">Scrapped on location</option>
                        </select>
                      </div>
                    )}

                    {customerRelocateDeviceError ? (
                      <div style={styles.errorText}>{customerRelocateDeviceError}</div>
                    ) : null}
                  </div>

                  <div style={styles.customerContactEditorActions}>
                    <div style={styles.customerContactEditorActionGroup}>
                      <button
                        type="button"
                        style={{ ...styles.quickViewBoxButton, ...styles.quickViewBoxButtonPrimary }}
                        onClick={() => void handleSaveCustomerRelocateDevice()}
                        disabled={
                          savingCustomerRelocateDevice ||
                          !customerRelocateDeviceForm.deviceId ||
                          (customerRelocateDeviceForm.action === 'move' &&
                            relocateLocationOptions.length === 0)
                        }
                      >
                        {savingCustomerRelocateDevice ? 'Saving...' : 'Save Movement'}
                      </button>
                      <button
                        type="button"
                        style={styles.quickViewBoxButton}
                        onClick={closeCustomerRelocateDeviceModal}
                        disabled={savingCustomerRelocateDevice}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div style={styles.detailSection}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Contacts</h3>
                <div style={styles.customerQuickViewActionGrid}>
                  <button
                    type="button"
                    style={styles.quickViewBoxButton}
                    onClick={() => setShowCustomerContacts(true)}
                  >
                    Contacts
                  </button>
                  <button
                    type="button"
                    style={styles.quickViewBoxButton}
                    onClick={() => setShowCustomerLocations(true)}
                  >
                    Locations
                  </button>
                </div>
              </div>
              <div style={styles.customerPrimaryContactCard}>
                <div style={styles.customerPrimaryContactHeader}>
                  <div style={styles.customerPrimaryContactCopy}>
                    <div style={styles.customerPrimaryContactEyebrow}>Primary Contact</div>
                    <div style={styles.customerPrimaryContactName}>
                      {activePrimaryContact?.fullName || 'No contact assigned'}
                    </div>
                    <div style={styles.customerPrimaryContactRole}>
                      {activePrimaryContact?.role || 'Customer contact'}
                    </div>
                  </div>
                  <div style={styles.customerPrimaryContactMetaItem}>
                    <span style={styles.customerPrimaryContactMetaLabel}>Email</span>
                    <span style={styles.customerPrimaryContactMetaValue}>
                      {activePrimaryContact?.email || 'Not provided'}
                    </span>
                  </div>
                  <div style={styles.customerPrimaryContactMetaItem}>
                    <span style={styles.customerPrimaryContactMetaLabel}>Phone</span>
                    <span style={styles.customerPrimaryContactMetaValue}>
                      {activePrimaryContact?.phone || activeCustomer.phone || 'Not provided'}
                    </span>
                  </div>
                  <div style={styles.customerPrimaryContactMetaItem}>
                    <span style={styles.customerPrimaryContactMetaLabel}>Location</span>
                    <span style={styles.customerPrimaryContactMetaValue}>
                      {activePrimaryContact?.locationId
                        ? activeCustomerLocations.find(
                            (location: any) =>
                              String(location?.id || '').trim() ===
                              String(activePrimaryContact.locationId || '').trim()
                          )?.name || getLocationSummary(activeCustomer)
                        : getLocationSummary(activeCustomer)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Devices</h3>
                <div style={styles.customerQuickViewActionGrid}>
                  {canManageSerializedDevices && (
                    <button
                      type="button"
                      style={styles.quickViewBoxButton}
                      onClick={openCustomerAddDeviceModal}
                    >
                      + Add Device
                    </button>
                  )}
                  {canManageSerializedDevices && (
                    <button
                      type="button"
                      style={styles.quickViewBoxButton}
                      onClick={openCustomerRelocateDeviceModal}
                    >
                      Relocate Device
                    </button>
                  )}
                  {canReadDevices && (
                    <button
                      type="button"
                      style={styles.quickViewBoxButton}
                      onClick={() => {
                        void handleOpenDevicesWorkspace();
                      }}
                    >
                      Open Devices Tab
                    </button>
                  )}
                </div>
              </div>
              {activeCustomerDeviceModels.length === 0 ? (
                <div style={styles.emptySubtle}>No devices assigned to this customer yet.</div>
              ) : (
                <div style={styles.customerDeviceModelGrid}>
                  {activeCustomerDeviceModels.map(model => (
                    <button
                      key={model.key}
                      type="button"
                      style={styles.customerDeviceModelButton}
                      onClick={() => openCustomerDeviceModelModal(model)}
                    >
                      <div style={styles.customerDeviceModelTopRow}>
                        <div style={styles.customerDeviceModelSummary}>
                          <div style={styles.customerDeviceModelCount}>{model.deviceCount}</div>
                          <div style={styles.customerDeviceModelLabel}>{model.label}</div>
                        </div>
                        <div style={styles.customerDeviceModelThumb}>
                          {buildDeviceModelImageSrc(model.imageUrl) ? (
                            <img
                              src={buildDeviceModelImageSrc(model.imageUrl)}
                              alt={model.label}
                              style={styles.customerDeviceModelThumbImage}
                            />
                          ) : (
                            <div style={styles.customerDeviceModelThumbFallback}>N/A</div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.detailSection}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Cases</h3>
                <button
                  type="button"
                  style={styles.quickViewBoxButton}
                  onClick={() => openCustomerCasesWorkspace(activeCustomer.id)}
                >
                  Open Cases
                </button>
              </div>
              <div style={styles.customerQuickViewStatusGrid}>
                <div style={styles.customerQuickViewCountCard}>
                  <div style={styles.customerQuickViewCountNumber}>{activeCustomerCaseStatuses.open}</div>
                  <div style={styles.customerQuickViewCountLabel}>Open</div>
                </div>
                <div style={styles.customerQuickViewCountCard}>
                  <div style={styles.customerQuickViewCountNumber}>{activeCustomerCaseStatuses.inProgress}</div>
                  <div style={styles.customerQuickViewCountLabel}>In Progress</div>
                </div>
                <div style={styles.customerQuickViewCountCard}>
                  <div style={styles.customerQuickViewCountNumber}>{activeCustomerCaseStatuses.onHold}</div>
                  <div style={styles.customerQuickViewCountLabel}>On Hold</div>
                </div>
                <div style={styles.customerQuickViewCountCard}>
                  <div style={styles.customerQuickViewCountNumber}>{activeCustomerCaseStatuses.resolved}</div>
                  <div style={styles.customerQuickViewCountLabel}>Resolved</div>
                </div>
                <div style={styles.customerQuickViewCountCard}>
                  <div style={styles.customerQuickViewCountNumber}>{activeCustomerCaseStatuses.closed}</div>
                  <div style={styles.customerQuickViewCountLabel}>Closed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});
